<template>
  <!-- La salida va al lado de la entrada, y no es decoración: la ruta es `keepAlive`, así que
       salir de aquí —con Atrás, con el botón físico o por una navegación del programa— no destruye
       nada y lo que estuviera pedido seguía siendo la petición vigente de su carril (ver
       `soloMandaLaDeDelante`). -->
  <f7-page :page-content="true" name="vacaciones" @page:beforein="entrar"
           @page:beforeout="salirDeLaPantalla">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('misVacaciones.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <!-- El desplegable de años de la cabecera. Nativo a propósito: en ancho de móvil el
             selector del sistema es el que mejor se usa con el pulgar.

             Tres cosas que lo hacen imposible de confundir con un recuadro blanco vacío, que es
             como lo vio dirección el 10/10/2026:

             · va con su rótulo «Año» delante, así que aunque no tuviera ningún año se sabría qué
               es y no habría que adivinarlo;
             · no se pinta mientras no haya ni un año que elegir —antes de la primera respuesta la
               lista está vacía, y un `select` sin `option` es exactamente un recuadro vacío—;
             · y el año elegido lo dice cada `option` con su `selected`, no el `select` con su
               `value`. Vue pone las props del elemento ANTES de parchear sus hijos, así que al
               llegar la primera respuesta se le fijaba el valor «2026» cuando todavía no existía
               la opción 2026 y el navegador se quedaba sin ninguna seleccionada.

             #1119 · Y a quien aprueba le dice en qué año le esperan solicitudes de su equipo:
             «2027 (2 por resolver)». Sin eso, las vacaciones de enero pedidas en octubre no
             salían al abrir la bandeja, que es del año que corre (ver `porResolverDeCadaAnio`). -->
        <label v-if="aniosDisponibles.length" class="selector-anio">
          <span class="selector-anio-rotulo">{{ $t('misVacaciones.anio') }}</span>
          <select :value="anio" @change="cambiarAnio($event)">
            <option v-for="a in aniosDisponibles" :key="a" :value="a" :selected="a === anio">{{ etiquetaAnio(a) }}</option>
          </select>
        </label>
        <f7-button icon-f7="arrow_clockwise" :text="$t('misVacaciones.recargar')" class="display-flex" @click="cargar"></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <!-- «Mis solicitudes» la ve todo el personal. Las cuatro de aprobación solo aparecen a quien
         figura como aprobador o suplente en alguna ficha, y eso lo dice el SERVIDOR
         (`apruebaAAlguien`): deducirlo aquí del rol sería volver a interpretar D3 en el front,
         donde el permiso de aprobar no es un rol sino estar escrito en una ficha. -->
    <f7-toolbar tabbar top>
      <f7-link
        v-for="p in pestanas"
        :key="p.clave"
        tab-link
        :class="{ 'tab-link-active': pestana === p.clave }"
        @click="cambiarPestana(p.clave)"
      >{{ p.titulo }}</f7-link>
    </f7-toolbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('misVacaciones.noAutorizado') }}</p>
    </f7-block>

    <!-- Las pestañas de aprobación. Cuelgan de `bandeja` y no de `resumen`: son dos peticiones
         distintas porque son dos cosas distintas —lo mío y lo de mi equipo—, y cada pestaña
         recarga la suya.

         Se le pasa `bandejaVigente` y NO `bandeja`: lo que se pinta es la respuesta de la pestaña
         y el año que se están mirando, no la última que llegó (ver `respuestaVigente`). -->
    <bandeja-aprobacion
      v-else-if="pestana !== 'MIS'"
      :pestana="pestana"
      :datos="bandejaVigente"
      :cargando="cargandoBandeja"
      @resuelta="resuelta"
      @anular="abrirAnulacionDirecta"
    ></bandeja-aprobacion>

    <template v-else-if="resumenVigente">
      <!-- Las cinco tarjetas de la maqueta. El desglose va escrito debajo del número: es lo que
           permite entender de dónde salen los días sin preguntar a RRHH. -->
      <f7-block strong class="tarjetas">
        <div class="tarjeta">
          <div class="tarjeta-titulo">{{ $t('misVacaciones.totales') }} {{ anio }}</div>
          <div class="tarjeta-numero">{{ totales }}</div>
          <div class="tarjeta-pie">{{ desgloseTotales }}</div>
        </div>
        <div class="tarjeta">
          <div class="tarjeta-titulo"><i class="sw sw-disfrutadas"></i> {{ $t('misVacaciones.disfrutadas') }}</div>
          <div class="tarjeta-numero">{{ saldo.disfrutados || 0 }}</div>
          <div class="tarjeta-pie">{{ $t('misVacaciones.pieDisfrutadas') }}</div>
        </div>
        <div class="tarjeta">
          <div class="tarjeta-titulo"><i class="sw sw-confirmadas"></i> {{ $t('misVacaciones.confirmadas') }}</div>
          <div class="tarjeta-numero">{{ saldo.confirmados || 0 }}</div>
          <div class="tarjeta-pie">{{ $t('misVacaciones.pieConfirmadas') }}</div>
        </div>
        <div class="tarjeta">
          <div class="tarjeta-titulo"><i class="sw sw-solicitadas"></i> {{ $t('misVacaciones.solicitadas') }}</div>
          <div class="tarjeta-numero">{{ saldo.solicitados || 0 }}</div>
          <div class="tarjeta-pie">{{ $t('misVacaciones.pieSolicitadas') }}</div>
        </div>
        <div class="tarjeta tarjeta-destacada">
          <div class="tarjeta-titulo">{{ $t('misVacaciones.disponibles') }}</div>
          <!-- En rojo cuando es negativo (#70): el saldo puede quedar por debajo de cero cuando
               ADMIN o DIRECTOR registran vacaciones ya disfrutadas, y entonces el número grande no
               puede parecer un saldo normal. -->
          <div class="tarjeta-numero" :class="{ 'numero-negativo': saldoNegativo }">{{ saldo.saldo || 0 }}</div>
          <!-- «para solicitar» solo si de verdad se pueden solicitar: con el botón apagado, esta
               tarjeta y el motivo de abajo decían lo contrario la una del otro, que es el mismo
               defecto que un botón mudo contado dos veces (§9). Ver `pieDisponibles`. -->
          <div class="tarjeta-pie">{{ pieDisponibles }}</div>
        </div>
      </f7-block>

      <!-- De dónde sale el número cuando lo confirmó Laboral (#682). Va aquí y no en el pie de
           la tarjeta porque es una frase, y va UNA vez: es la respuesta a «¿por qué tengo 6 y
           no 23?», y repetida en cada tarjeta uno de los sitios acabaría viejo (§9). -->
      <f7-block v-if="textoSaldoPartida" strong class="saldo-partida">
        <p>{{ textoSaldoPartida }}</p>
      </f7-block>

      <!-- Días excepcionales que reconoció RRHH. Van aparte de las tarjetas para poder decir
           hasta cuándo valen: por incapacidad temporal pueden ir más allá del 31 de marzo. -->
      <f7-block v-if="bolsaExcepcionales" strong class="excepcionales">
        <p>{{ textoExcepcionales }}</p>
      </f7-block>

      <!-- #69 · los días que volvieron por una anulación por incapacidad temporal. Aparte por lo
           mismo que los de arriba, y además es lo que pidió dirección: su plazo es de 18 meses
           desde el final del año (art. 38.3 del Estatuto) y no el 31 de marzo del siguiente, así
           que si estuvieran dentro de «Disponibles» parecería que caducan cuando no caducan. -->
      <f7-block v-if="lineasIncapacidadTemporal.length" strong class="excepcionales">
        <p v-for="(linea, i) in lineasIncapacidadTemporal" :key="'it-' + i">{{ linea }}</p>
      </f7-block>

      <!-- La barra con los cuatro tramos de color más el resto disponible. -->
      <f7-block strong class="barra-wrapper">
        <div class="barra" :title="tituloBarra">
          <div class="tramo tramo-disfrutadas" :style="{ width: ancho(saldo.disfrutados) }"></div>
          <div class="tramo tramo-confirmadas" :style="{ width: ancho(saldo.confirmados) }"></div>
          <div class="tramo tramo-solicitadas" :style="{ width: ancho(saldo.solicitados) }"></div>
          <!-- El disponible de la barra, que con el saldo en negativo es CERO: ver
               `disponiblesEnLaBarra`. El número de verdad —en rojo— lo dice la tarjeta. -->
          <div class="tramo tramo-disponibles" :style="{ width: ancho(disponiblesEnLaBarra) }"></div>
        </div>
      </f7-block>

      <f7-block class="acciones">
        <!-- **Por qué no se puede pulsar, quién lo desbloquea y dónde — junto al control.**
             Regla de dirección del 05/10/2026 (§9 del `CLAUDE.md` del orquestador), que salió con
             este mismo botón: el motivo estaba en un aviso al principio de la pantalla y el botón
             se quedaba mudo, así que al pulsarlo «no pasaba nada» y nadie relacionaba lo uno con
             lo otro (dirección volvió a encontrarlo el 10/10/2026 probando esta ventana).

             Se enseñan TODOS los impedimentos y no solo el primero: si le faltan dos cosas,
             resolver una deja el botón igual de apagado. Vienen del servidor con las tres partes
             ya escritas (`RhImpedimentoSolicitarDTO`), que es lo que impide que esta pantalla y
             el error del envío cuenten lo mismo de dos maneras. -->
        <div v-if="impedimentos.length" class="impedimentos">
          <p class="impedimentos-titulo">{{ $t('misVacaciones.noPuedesSolicitar') }}</p>
          <div v-for="imp in impedimentos" :key="imp.codigo" class="impedimento">
            <p class="impedimento-porque">{{ imp.mensaje }}</p>
            <p v-if="imp.quien" class="impedimento-quien">
              {{ $t('misVacaciones.loDesbloquea', { quien: imp.quien }) }}<template v-if="imp.donde">: {{ imp.donde }}</template>
            </p>
          </div>
        </div>
        <f7-button
          fill
          icon-f7="plus"
          :text="$t('misVacaciones.solicitar')"
          :disabled="!resumenVigente.puedeSolicitar"
          class="display-flex"
          @click="abrirSolicitud"
        ></f7-button>
        <!-- «Registrar en nombre de…» (#70): **solo se dibuja si se puede usar** (§9, regla 1 de
             dirección del 05/10/2026). A un aprobador corriente no se le enseña apagado ni con un
             motivo: no es algo que pueda desbloquear nadie, es que no es para él, y un control que
             nunca se va a poder pulsar solo le hace creer que le han quitado permisos. Quien lo
             decide es `PermisosVacaciones.registrarEnNombreDe()`, que resuelve la ruta y llega por
             `prop`; el gate de verdad es el servidor, que contesta 403 (I3).

             Se pinta con el permiso **de ahora** y no con el `prop`: la ruta lleva `keepAlive`, así
             que la pantalla no se vuelve a montar y el `prop` se queda en el de la sesión que la
             montó (ver `permisosDeLaRuta`). -->
        <f7-button
          v-if="puedeRegistrarEnNombreDeAhora"
          icon-f7="person_badge_plus"
          :text="$t('registrarVacaciones.abrir')"
          class="display-flex"
          @click="registroAbierto = true"
        ></f7-button>
      </f7-block>

      <!-- Las tres listas por estado. Salen de la misma lista de solicitudes repartida por su
           estado: el servidor ya dice en cuál está cada una. -->
      <template v-for="grupo in grupos">
        <f7-block-title :key="grupo.clave + '-t'">{{ grupo.titulo }}</f7-block-title>
        <f7-block :key="grupo.clave" strong class="lista">
          <div v-for="s in grupo.solicitudes" :key="s.id" class="linea" :class="'borde-' + grupo.clave">
            <div class="linea-datos">
              <div class="linea-fechas">{{ fechasDe(s) }}</div>
              <div class="linea-detalle">{{ detalleDe(s) }}</div>
              <!-- #69 · el enlace de «cambiar fechas»: no hay tal acción, es anular y volver a
                   pedir, y las dos solicitudes se nombran la una a la otra (punto 5). -->
              <div v-if="s.sustituyeA" class="linea-enlace">
                {{ $t('anularVacaciones.sustituyeA', { codigo: s.sustituyeA }) }}
              </div>
              <div v-if="s.sustituidaPor" class="linea-enlace">
                {{ $t('anularVacaciones.sustituidaPor', { codigo: s.sustituidaPor }) }}
              </div>
              <!-- #69 · lo que pasa con una petición de anulación que ya se ha mandado. Mientras
                   espera, los días siguen aquí y en azul, así que sin esta línea la persona vería
                   sus días intactos y no sabría si su petición llegó. -->
              <div v-if="s.peticionAnulacion" class="linea-peticion" :class="'peticion-' + claveAnulacion(s)">
                {{ textoPeticion(s) }}
              </div>
              <!-- #69 · y los días que ya le anularon de esta solicitud, con su motivo: es lo que
                   explica por qué un tramo de cuatro días descuenta tres ahora y antes no. -->
              <div v-for="dia in diasAnulados(s)" :key="'an-' + dia.fecha" class="linea-anulado">
                {{ corta(dia.fecha) }} · {{ dia.detalle }}
              </div>
            </div>
            <div class="linea-acciones">
              <span class="etiqueta" :class="'etiqueta-' + grupo.clave">{{ etiquetaDe(s) }}</span>
              <!-- «Anular solicitud» solo donde el servidor dice que se puede: el solicitante,
                   mientras siga pendiente (D-ANU). Una ya aprobada no se quita, se pide: ese es
                   el botón de al lado.

                   Mientras una anulación está en vuelo todos se apagan y el de su fila se rotula
                   «Anulando…»: así el botón apagado dice por qué lo está (§9 del `CLAUDE.md` del
                   orquestador) y no hace falta un aviso aparte.

                   Y el rótulo sale de `anulandose`, que pregunta por el cerrojo de la solicitud y
                   no solo por `anulandoId`: el cerrojo vive en el módulo (`unaAccionALaVez`), así
                   que sobrevive a que esta pantalla se recree y es lo que sigue explicando el
                   botón apagado cuando el POST todavía viaja. -->
              <f7-button
                v-if="s.puedeAnular"
                color="red"
                :text="anulandose(s) ? $t('misVacaciones.anulando') : $t('misVacaciones.anular')"
                :disabled="anulandoId !== null || anulandose(s)"
                class="display-flex boton-anular"
                @click="anular(s)"
              ></f7-button>
              <!-- #69 · y «Pedir anulación» para una ya aprobada: eso no se quita, se pide. Sale
                   donde el servidor dice que se puede —es suya, está confirmada, le quedan días
                   futuros y no tiene ya otra petición esperando—. -->
              <f7-button
                v-if="s.puedePedirAnulacion"
                color="orange"
                :text="$t('anularVacaciones.pedirAnulacion')"
                class="display-flex boton-anular"
                @click="abrirPeticion(s)"
              ></f7-button>
            </div>
          </div>
          <p v-if="!grupo.solicitudes.length" class="vacio">{{ grupo.vacio }}</p>
        </f7-block>
      </template>

      <f7-block-title>{{ $t('misVacaciones.aniosAnteriores') }}</f7-block-title>
      <f7-block strong class="tabla-wrapper">
        <table class="tabla-vacaciones">
          <thead>
            <tr>
              <th>{{ $t('misVacaciones.anio') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.devengadas') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.disfrutadasEseAnio') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.noDisfrutadas') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.usadasDespues') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.pendientes') }}</th>
              <th>{{ $t('misVacaciones.sePuedenUsarHasta') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- Un año que el módulo no llevaba no sale con ceros: sale con su motivo. «Devengadas
                 23 · Disfrutadas 0 · No disfrutadas 0» no puede ser verdad a la vez, y el 0 de «no
                 disfrutadas» hacía creer que esos 23 días se habían perdido (dirección,
                 10/10/2026). Sigue siendo UNA fila por año, que es lo que pidió el 09/10: quitarla
                 dejaría la tabla sin explicar por qué falta el año que esa persona espera ver.
                 Qué años no lleva el módulo lo decide el servidor (`sinDatos`). -->
            <tr v-for="fila in resumenVigente.aniosAnteriores" :key="fila.anio">
              <template v-if="fila.sinDatos">
                <td>{{ fila.anio }}</td>
                <td colspan="6" class="sin-datos">{{ fila.motivoSinDatos }}</td>
              </template>
              <template v-else>
              <td>{{ fila.anio }}</td>
              <td class="col-centro">{{ fila.devengadas }}</td>
              <td class="col-centro">{{ fila.disfrutadasEseAnio }}</td>
              <td class="col-centro">{{ fila.noDisfrutadas }}</td>
              <td class="col-centro">{{ fila.usadasDespues || '—' }}</td>
              <!-- Si un año posterior quedó en negativo, el servidor cobra la diferencia de lo que
                   quedara vivo de este arrastre (`retiradosPorDeuda`). Esos días no se disfrutaron
                   ni caducaron, así que sin esta línea la fila perdería días por el camino: «no
                   disfrutadas 5 · usadas después — · pendientes 3» y los otros 2 en ningún sitio.
                   Lo normal es que no venga ninguno y entonces no se pinta nada. -->
              <td class="col-centro col-fuerte">
                {{ fila.pendientes }}
                <div v-if="fila.retiradosPorDeuda" class="caducidad-extra">
                  {{ $t('misVacaciones.retiradosPorDeuda', { dias: fila.retiradosPorDeuda }) }}
                </div>
              </td>
              <!-- Un año puede vencer en dos fechas: los días ordinarios el 31 de marzo y los de
                   una baja médica más tarde, que es ley (art. 38.3 ET). La casilla dice la próxima
                   en la que pierde días y las demás van en una línea debajo, con sus días y su
                   motivo: con una sola fecha ninguna era verdad para todos los días (decisión de
                   dirección del 09/10/2026). El reparto lo manda el servidor en `caducidades`. -->
              <td :class="{ caducado: fila.caducado }">
                {{ fila.sePuedenUsarHasta ? fecha(fila.sePuedenUsarHasta) : '—' }}
                <span v-if="fila.caducado">· {{ $t('misVacaciones.caducado') }}</span>
                <div v-for="(c, i) in otrasCaducidades(fila)" :key="i" class="caducidad-extra"
                     :class="{ caducado: c.caducada }">
                  {{ textoCaducidad(c) }}
                </div>
              </td>
              </template>
            </tr>
            <tr v-if="!resumenVigente.aniosAnteriores.length">
              <td colspan="7" class="vacio">{{ $t('misVacaciones.sinAniosAnteriores') }}</td>
            </tr>
          </tbody>
        </table>
      </f7-block>

      <f7-block-title>{{ $t('misVacaciones.historial') }}</f7-block-title>
      <f7-block strong class="tabla-wrapper">
        <table class="tabla-vacaciones">
          <thead>
            <tr>
              <th>{{ $t('misVacaciones.solicitud') }}</th>
              <th>{{ $t('misVacaciones.fechas') }}</th>
              <th class="col-centro">{{ $t('misVacaciones.dias') }}</th>
              <th>{{ $t('misVacaciones.estado') }}</th>
              <th>{{ $t('misVacaciones.ultimoMovimiento') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in resumenVigente.solicitudes" :key="'h-' + s.id">
              <td class="col-codigo">{{ s.codigo }}</td>
              <td>{{ fechasDe(s) }}</td>
              <td class="col-centro">{{ s.diasQueDescuenta }}</td>
              <td>
                <span class="etiqueta" :class="'etiqueta-' + claveEstado(s.estado)">{{ etiquetaDe(s) }}</span>
                <!-- La marca «registrada por» también aquí (#70, punto 6): el histórico de la
                     persona es donde se mira meses después por qué hay unos días apuntados. -->
                <span v-if="s.registradaPor" class="etiqueta etiqueta-registrada">{{ registradaPor(s) }}</span>
              </td>
              <td>
                <template v-if="ultimoMovimiento(s)">
                  {{ fechaHora(ultimoMovimiento(s).fecha) }} ·
                  {{ accion(ultimoMovimiento(s)) }}
                  <div v-if="ultimoMovimiento(s).motivo || s.comentario" class="motivo">
                    «{{ ultimoMovimiento(s).motivo || s.comentario }}»
                  </div>
                </template>
                <template v-else>—</template>
              </td>
            </tr>
            <tr v-if="!resumenVigente.solicitudes.length">
              <td colspan="5" class="vacio">{{ $t('misVacaciones.sinSolicitudes') }}</td>
            </tr>
          </tbody>
        </table>
      </f7-block>

      <p v-if="pieAprobador" class="pie">{{ pieAprobador }}</p>
    </template>

    <!-- Sin `v-if`: este popup tiene estado propio —los tramos escritos, las normas marcadas y el
         envío en vuelo— y colgarlo del resumen lo destruía justo cuando ese estado importaba.
         Vaciar el resumen es de todos los días aquí: lo hace cambiar de año y lo hace el `limpiar`
         del carril cuando una carga falla. Con el POST a medias, el formulario se destruía, su
         `enviada` ya no recargaba el resumen y, al volver el popup, el botón «Enviar solicitud» no
         hacía nada porque el adaptador cortaba el segundo intento (revisión CODEX del PR #1733).
         Es un popup: lo que lo enseña o lo esconde es `opened`, no que esté montado.

         Y `autoconfirma` sale de `resumen` y no de `resumenVigente` por lo mismo que los años del
         desplegable: no es un dato del año que se mira, es de la persona —si tiene aprobador
         superior o no—, así que apagarlo mientras llega la respuesta de otro año le cambiaría al
         formulario, abierto y a medio escribir, si el motivo es obligatorio. -->
    <solicitar-vacaciones
      ref="formulario"
      :opened="solicitudAbierta && pestana === 'MIS'"
      :disponibles="saldo.saldo || 0"
      :autoconfirma="!!(resumen && resumen.autoconfirma)"
      :reloj="reloj"
      @cerrar="solicitudAbierta = false"
      @enviada="trasEnviar"
    ></solicitar-vacaciones>

    <!-- La ventana de registrar en nombre de otra persona (#70). Montada solo para quien puede
         usarla, por lo mismo que el botón; y con el MISMO registro del reloj que el formulario de
         solicitud (`:reloj`), que es lo que impide que dos vigilantes del mismo modal se apaguen
         el uno al otro. -->
    <registrar-en-nombre
      v-if="puedeRegistrarEnNombreDeAhora"
      ref="registro"
      :opened="registroAbierto"
      :reloj="reloj"
      @cerrar="registroAbierto = false"
      @registrada="trasRegistrar"
    ></registrar-en-nombre>

    <!-- #69 · las dos ventanas del flujo de anulación. Cuelgan de la pantalla y no de la lista o
         de la bandeja porque son popups: dentro de un `v-for` se montaría una por fila.

         Con su `ref`, que es lo que las apunta a la limpieza común (`POPUPS`). El indicador de
         espera de su envío no se lo pasa esta pantalla: lo lleva `accionDeLaSesion`, que es por
         donde escriben las dos, y de ahí sale con dueño —un testigo por petición— igual que el del
         registro de cargas de aquí. -->
    <pedir-anulacion
      ref="anulacion"
      :opened="!!solicitudAAnular"
      :solicitud="solicitudAAnular"
      :aprobador="aprobadorDe(solicitudAAnular)"
      @cerrar="solicitudAAnular = null"
      @enviada="trasPedirAnulacion"
    ></pedir-anulacion>

    <anular-vacaciones
      ref="anulacionDirecta"
      :opened="!!solicitudAAnularDirecto"
      :solicitud="solicitudAAnularDirecto"
      @cerrar="solicitudAAnularDirecto = null"
      @anulada="trasAnularDirecto"
    ></anular-vacaciones>
  </f7-page>
</template>
<style scoped>
.pie { font-size: 12px; color: #8e8e93; margin: 0 16px 8px; }
/* El rótulo va en el blanco de la barra y el desplegable con su color escrito: la barra de esta
   aplicación es azul oscuro con texto blanco (`--f7-bars-text-color`), así que un control que no
   diga de qué color es su texto se lee mal o no se lee. */
.selector-anio { display: inline-flex; align-items: center; gap: 4px; margin-right: 8px; }
.selector-anio-rotulo { font-size: 11px; color: #fff; text-transform: uppercase; }
.selector-anio select {
  padding: 2px 4px;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  background-color: #fff;
  color: #1a2744;
  font-size: 14px;
}

/* El panel del «por qué no se puede pulsar» va pegado al botón, dentro del mismo bloque: es lo
   que hace que se lean como una sola cosa (§9). */
.impedimentos {
  width: 100%;
  margin-bottom: 8px;
  padding: 8px 10px;
  box-sizing: border-box;
  border-radius: 6px;
  background-color: #fff4e5;
  border-left: 4px solid #d98324;
}
.impedimentos-titulo { margin: 0 0 4px; font-size: 13px; color: #8a4b00; font-weight: bold; }
.impedimento { margin: 4px 0 0; }
.impedimento-porque { margin: 0; font-size: 13px; color: #8a4b00; font-weight: bold; }
.impedimento-quien { margin: 2px 0 0; font-size: 12px; color: #8a4b00; }
.excepcionales { background-color: #eef6ff; border-left: 4px solid #2196f3; }
.excepcionales p { margin: 4px 0; font-size: 13px; color: #11508c; }

/* El saldo confirmado por Laboral (#682) se pinta como los días excepcionales: es el mismo tipo
   de aviso —de dónde sale un número que no es el del convenio— y darle otro color haría parecer
   que uno de los dos es un problema. */
.saldo-partida { background-color: #eef6ff; border-left: 4px solid #2196f3; }
.saldo-partida p { margin: 4px 0; font-size: 13px; color: #11508c; }

/* Las tarjetas se reparten el ancho y, en móvil, se envuelven solas: `flex-wrap` con un ancho
   mínimo en vez de una media query, para que valga igual en la app Cordova y en el escritorio. */
.tarjetas { display: flex; flex-wrap: wrap; gap: 8px; }
.tarjeta {
  flex: 1 1 140px;
  min-width: 140px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 10px;
  background-color: #fff;
}
.tarjeta-titulo { font-size: 11px; color: #64748b; letter-spacing: 0.3px; }
.tarjeta-numero { font-size: 28px; font-weight: bold; color: #1a2744; line-height: 1.1; }
.tarjeta-pie { font-size: 11px; color: #8e8e93; }
.tarjeta-destacada { background-color: #1a2744; }
.tarjeta-destacada .tarjeta-titulo,
.tarjeta-destacada .tarjeta-pie { color: #c7d2e5; }
.tarjeta-destacada .tarjeta-numero { color: #fff; }
/* El saldo negativo, en rojo (#70). Sobre el azul oscuro de la tarjeta destacada hace falta un
   rojo claro: el rojo de los avisos de la aplicación no tiene contraste suficiente ahí. */
.tarjeta-destacada .tarjeta-numero.numero-negativo { color: #ff8a80; }
.tarjeta-numero.numero-negativo { color: #d32f2f; }
/* La marca «registrada por», al lado de la etiqueta de estado: en gris, porque no es un estado. */
.etiqueta-registrada { background-color: #e8eaf0; color: #5a6472; }

/* Los mismos colores que los bordes del calendario (D-UX): verde realizada, azul confirmada,
   amarillo pendiente de confirmar. Que la tarjeta y el día del calendario se vean del mismo
   color es lo que permite relacionarlos sin leyenda. */
.sw { display: inline-block; width: 10px; height: 10px; border-radius: 2px; vertical-align: middle; }
.sw-disfrutadas { background-color: #4cd964; }
.sw-confirmadas { background-color: #2196f3; }
.sw-solicitadas { background-color: #f0c419; }

.barra-wrapper { padding-top: 4px; padding-bottom: 4px; }
.barra { display: flex; height: 10px; border-radius: 5px; overflow: hidden; background-color: #e9ecef; }
.tramo { height: 100%; }
.tramo-disfrutadas { background-color: #4cd964; }
.tramo-confirmadas { background-color: #2196f3; }
.tramo-solicitadas { background-color: #f0c419; }
.tramo-disponibles { background-color: #cfd4da; }

/* En columna y pegado a la derecha, que es donde estaba el botón: el motivo queda justo encima y
   se envuelve solo en ancho de móvil. */
.acciones { display: flex; flex-direction: column; align-items: flex-end; }

.lista { padding: 0; }
.linea {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  border-left: 4px solid #cfd4da;
  border-bottom: 1px solid #f0f0f0;
  padding: 8px 10px;
}
.linea:last-child { border-bottom: none; }
.borde-solicitadas { border-left-color: #f0c419; }
.borde-confirmadas { border-left-color: #2196f3; }
.borde-disfrutadas { border-left-color: #4cd964; }
.linea-datos { flex: 1 1 200px; }
.linea-fechas { font-size: 14px; font-weight: bold; color: #1a2744; }
.linea-detalle { font-size: 12px; color: #8e8e93; }
/* #69 · el enlace entre la anulada y la que la sustituye, el estado de la petición y los días ya
   anulados. Van debajo del detalle, en la misma columna: son cosas de ESA solicitud. */
.linea-enlace { font-size: 12px; color: #11508c; }
.linea-peticion { font-size: 12px; font-weight: bold; }
.peticion-pendiente { color: #8a4b00; }
.peticion-aceptada { color: #1b7a34; }
.peticion-cerrada { color: #64748b; }
.linea-anulado { font-size: 11px; color: #8e8e93; text-decoration: line-through; }
.linea-acciones { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.boton-anular { font-size: 12px; }

.etiqueta {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: bold;
  border: 1px solid #cfd4da;
  color: #64748b;
}
.etiqueta-solicitadas { border-color: #f0c419; color: #8a6d00; background-color: #fffbe6; }
.etiqueta-confirmadas { border-color: #2196f3; color: #11508c; background-color: #eef6ff; }
.etiqueta-disfrutadas { border-color: #4cd964; color: #1b7a34; background-color: #eefaf0; }

.tabla-wrapper { overflow-x: auto; }
.tabla-vacaciones { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-vacaciones th,
.tabla-vacaciones td {
  border-bottom: 1px solid #e0e0e0;
  padding: 4px 6px;
  text-align: left;
  vertical-align: top;
}
.tabla-vacaciones th { background-color: #f4f5f7; font-weight: bold; }
.tabla-vacaciones .col-centro { text-align: center; }
.tabla-vacaciones .col-codigo { font-family: monospace; white-space: nowrap; }
.tabla-vacaciones .col-fuerte { font-weight: bold; }
.tabla-vacaciones .caducado { color: #b26a00; }
/* La línea de la segunda caducidad: más pequeña que la fecha principal, pero sin bajar del gris
   que se lee (4,5:1 sobre blanco). Y en ámbar cuando esos días ya se perdieron, igual que la
   fecha de arriba: el .caducado de al lado no basta, porque las dos reglas empatan en
   especificidad y gana la última. */
.tabla-vacaciones .caducidad-extra { font-size: 11px; color: #6d6d72; }
.tabla-vacaciones .caducidad-extra.caducado { color: #b26a00; }
.tabla-vacaciones .motivo { color: #8e8e93; font-style: italic; }
/* La fila de un año del que no se sabe nada: el motivo ocupa las seis columnas de cifras. */
.tabla-vacaciones .sin-datos { color: #6d6d72; font-style: italic; }
.vacio { text-align: center; color: #8e8e93; padding: 16px; font-size: 13px; }
</style>
<script>
import SolicitarVacaciones from "./solicitar-vacaciones.vue";
import BandejaAprobacion from "./bandeja-aprobacion.vue";
import RegistrarEnNombre from "./registrar-en-nombre.vue";
import PedirAnulacion from "./pedir-anulacion.vue";
import AnularVacaciones from "./anular-vacaciones.vue";
import RhSolicitudesVacaciones from "./../../js/db/RhSolicitudesVacaciones.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";
import RhBandejaVacaciones from "./../../js/db/RhBandejaVacaciones.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import respuestaVigente from "./../../js/utils/respuestaVigente.js";
import { pedirEspera, soltarEspera } from "./../../js/utils/esperaGlobal.js";
import { unaAccionALaVez, claveDeSolicitud } from "./../../js/utils/unaAccionALaVez.js";
import soloMandaLaDeDelante, {
  avisar, MOTIVO_CIERRE_DE_SESION, MOTIVO_SALIDA_DE_PANTALLA
} from "./../../js/utils/soloMandaLaDeDelante.js";
import permisosDeLaRuta from "./../../js/utils/permisosDeLaRuta.js";
import PermisosVacaciones from "./permisos.js";

/**
 * **Los popups que viven dentro de esta pantalla, enumerados en UN sitio**: su `ref` y la
 * propiedad que los abre.
 *
 * Un popup de aquí no se entera por su cuenta de que la pantalla se va —el `page:beforeout` lo
 * emite la página— y tampoco puede cerrarse solo, porque quien manda sobre su `opened` es esta
 * pantalla. Las dos cosas se hacían nombrando a mano al formulario de solicitud, así que la
 * ventana de registrar en nombre de otra persona nació sin ninguna de las dos: con `keepAlive`
 * se quedaba abierta sobre el login o sobre la cuenta siguiente, y su vista previa en vuelo
 * todavía podía poblarla con la plantilla de la sesión anterior (revisión CODEX del PR #1737).
 *
 * Con la lista aquí, añadir un popup —las pestañas del aprobador de la #59 traen el suyo— es una
 * línea, y lo que decide qué se le avisa y qué se le cierra es `dejarDeMandar`, en un solo sitio.
 *
 * Que la línea haga falta y no se añada sola es lo que volvió a pasar con las dos ventanas de la
 * #69 (revisión CODEX del PR #1748, ronda 6): nacieron sin `ref` y sin entrada aquí, así que con
 * `keepAlive` se quedaban abiertas sobre el login con la solicitud, los días y el motivo de la
 * cuenta anterior. Por eso lo vigila ahora una prueba —`scripts/prueba-popups-apuntados.js`—, que
 * lee el template de esta pantalla y falla si hay un popup del módulo que no esté en esta lista:
 * la lista sigue estando en un sitio, y olvidarse ya no es silencioso.
 *
 * `cerrado` es lo que hay que ponerle a `abierto` para que se cierre. No siempre es `false`: las
 * dos ventanas de la #69 se abren con la solicitud sobre la que van (`!!solicitudAAnular`), y
 * dejarles un `false` donde esperan un objeto o `null` las cerraría igual pero le cambiaría el tipo
 * a lo que la pantalla lee después.
 */
const POPUPS = [
  { ref: 'formulario', abierto: 'solicitudAbierta', cerrado: false },
  { ref: 'registro', abierto: 'registroAbierto', cerrado: false },
  { ref: 'anulacion', abierto: 'solicitudAAnular', cerrado: null },
  { ref: 'anulacionDirecta', abierto: 'solicitudAAnularDirecto', cerrado: null }
];

/**
 * #59 · Las cuatro pestañas de aprobación que existen, en el orden en que se enseñan, y el campo
 * de la respuesta del que sale su contador. «MIS» no está aquí: no es una pestaña de la bandeja,
 * no se pide al servidor y la ve todo el mundo.
 *
 * Está en un solo sitio porque la leen **dos** cosas que tienen que decir lo mismo: la barra de
 * arriba, que es la lista de pestañas que se pueden pulsar, y `pestanaPedida`, que es la lista de
 * las que se admiten en el `?pestana=` de la URL. Escritas por separado, una pestaña nueva se
 * añadiría a la barra y seguiría siendo «desconocida» al llegar por enlace.
 */
const PESTANAS_DE_APROBACION = [
  { clave: 'PENDIENTES', contador: 'pendientes' },
  { clave: 'ANULACIONES', contador: 'anulaciones' },
  { clave: 'CONFIRMADAS', contador: 'confirmadas' },
  { clave: 'TODAS', contador: 'todas' }
];

/**
 * #59 · La pestaña que pide la URL, **solo si es una de las que existen**; si no, «Mis
 * solicitudes».
 *
 * El `?pestana=` viene de fuera —un enlace guardado, uno compartido, uno escrito a mano— y lo que
 * trae acababa puesto tal cual en `pestana`. Con un valor que no existe (`?pestana=FOO`) la
 * pantalla quedaba inservible y sin decirlo: el servidor **no** responde un error, su `deTexto`
 * sirve Pendientes para todo lo que no entiende, así que la respuesta llegaba de una pestaña
 * distinta de la que la pantalla creía mirar y quedaba una bandeja vacía sin ninguna pestaña
 * marcada en la barra.
 *
 * Se admite en cualquier caja y con espacios de más (`?pestana=pendientes`) porque es exactamente
 * lo que el servidor admite: si ahí los dos no dicen lo mismo, se cae en la misma bandeja huérfana.
 */
function pestanaPedida(query) {
  const pedida = ((query && query.pestana) || '').trim().toUpperCase();
  return PESTANAS_DE_APROBACION.some(function(p) { return p.clave === pedida; }) ? pedida : 'MIS';
}

/**
 * Ventana «Mis vacaciones» (#58): el saldo del año con su desglose, las tres listas por estado,
 * lo que quedó de los años anteriores y el historial de solicitudes.
 *
 * **Esta pantalla solo muestra.** El cálculo, el saldo y las validaciones los hace el servidor
 * (#57) y llegan ya resueltos en una sola petición: aquí no se suma ni se resta nada, y lo único
 * que se decide es en qué lista va cada solicitud, que es su propio `estado`.
 *
 * El año se elige con el desplegable de la cabecera y cada cambio es una petición nueva: el saldo
 * de un año no se puede derivar del de otro.
 */
export default {
  components: {
    SolicitarVacaciones,
    BandejaAprobacion,
    RegistrarEnNombre,
    PedirAnulacion,
    AnularVacaciones
  },
  // Las tres formas de dejar de ser la pantalla de delante —cerrar sesión, salir de ella y
  // destruirse— acaban en `dejarDeMandar`. Están enumeradas en un solo sitio a propósito: repartidas
  // en los hooks de esta pantalla se cubrían dos de tres, y la ronda siguiente traía la que
  // faltaba (ver `soloMandaLaDeDelante`).
  //
  // Y una acción a la vez **por solicitud**: el cerrojo de `unaAccionALaVez` apaga «Anular»
  // mientras su POST viaja, igual que en la bandeja y en el calendario, y vive en el módulo —no en
  // esta instancia— porque es de la solicitud y dura lo que dura la petición. `anulandoId` sigue
  // diciendo CUÁL se está anulando desde aquí, que es lo que apaga los botones de las otras filas.
  //
  // Y «si esta persona puede registrar vacaciones en nombre de otra» (#70: solo ADMIN y DIRECTOR)
  // lo resuelve la ruta y llega por `prop`, para que la pantalla se monte ya sabiendo si dibuja el
  // botón en vez de decidirlo al pintarse con el menú de la sesión anterior (#65). El `prop` es el
  // valor de arranque y lo que se pinta es `puedeRegistrarEnNombreDeAhora`, que se vuelve a
  // preguntar en cada entrada: la ruta lleva `keepAlive` y la instancia sobrevive al cambio de
  // cuenta (ver `permisosDeLaRuta`).
  mixins: [
    soloMandaLaDeDelante,
    unaAccionALaVez,
    permisosDeLaRuta({ puedeRegistrarEnNombreDe: PermisosVacaciones.registrarEnNombreDe })
  ],
  data: function() {
    return {
      resumen: null,
      anio: null,
      // Los años del desplegable, FUERA del resumen: son de la persona, no del año que se está
      // mirando, así que tienen que sobrevivir a que se tire el resumen (ver `aniosDisponibles`).
      anios: [],
      error403: false,
      solicitudAbierta: false,
      /** #59 · la pestaña abierta. `MIS` es «Mis solicitudes», que es la que ve todo el mundo. */
      pestana: 'MIS',
      /** #59 · la respuesta de la bandeja del aprobador, con sus filas y sus cuatro contadores. */
      bandeja: null,
      /**
       * **Las cargas de esta pantalla que están en vuelo, por clave** (ver `empezarCarga`). De
       * aquí sale el «cargando» de la bandeja, así que es estado que se pinta y por eso vive en
       * `data`: antes era un registro suelto en `created` porque solo encendía el preloader.
       */
      cargas: {},
      /**
       * #59 · Si esta persona aprueba a alguien, que es lo que dibuja las cuatro pestañas. Lo dice
       * el servidor y vive FUERA de `bandeja`: una carga fallida vacía las filas, y colgada de
       * ellas la barra desaparecía —y con `keepAlive` no quedaba ninguna pestaña que pulsar para
       * volver a pedirla— hasta cerrar la aplicación.
       */
      apruebaAAlguien: false,
      registroAbierto: false,
      // Qué solicitud se está anulando ahora mismo, o `null`. Es estado que se pinta —apaga los
      // botones y rotula el de su fila—, así que va en `data`. Ver `anular`.
      anulandoId: null,
      /**
       * #69 · la solicitud sobre la que está abierta cada ventana de anulación, o `null`. Son dos
       * y no una porque son dos acciones distintas de dos personas distintas: pedir es del
       * solicitante («Mis solicitudes») y anular directamente es de quien aprueba (la bandeja).
       * Con una sola variable, abrir una habría cerrado la otra.
       */
      solicitudAAnular: null,
      solicitudAAnularDirecto: null
    };
  },
  created: function() {
    // Cambiar de año lanza una petición por pulsación y las respuestas no vuelven en orden: sin el
    // carril, la del año anterior puede llegar después y pintar su saldo encima del bueno.
    //
    // De quién era cada petición lo lleva el MISMO carril, por su sello de sesión (decisión de
    // dirección del 06/10/2026 en la #262: una sola decisión y un solo sitio, en vez de un
    // vigilante aparte al lado). Lo pone `comprobarSesion`.
    //
    // #472 · Y también lo que se ve MIENTRAS: el carril sabe que el resumen es de un año, lo tira
    // al pedir otro y lleva el reloj de esta lectura. Antes lo hacían `cambiarAnio` —vaciando a
    // mano— y los `exito`/`fallo` de `cargar` —soltando el reloj—, que es la garantía repartida
    // por llamante que la #58 dejó para el arreglo de raíz.
    const self = this;
    this.carril = carrilPeticiones({
      deAhora: function() { return self.anio; },
      // Se tira el resumen, que es de un año, pero NO los años del desplegable, que son de la
      // persona: viven en `anios` justamente para no irse con él (ver `aniosDisponibles`).
      vaciar: function() { self.resumen = null; },
      cargando: function(si) {
        if (si) self.cogerReloj('resumen');
        else self.soltarReloj(self.cargas.resumen);
      }
    });
    // #59 · un carril propio para la bandeja: cambiar de pestaña o de año relanza su petición, y
    // son dos lecturas distintas —lo mío y lo de mi equipo— que no pueden adelantarse la una a la
    // otra. El sello de sesión es el mismo de la pantalla: lo ponen `dejarDeMandar` y `entrar`.
    //
    // #472 · Su «cargando» lo lleva el carril. No se vacía al cambiar de pestaña: lo que se pinta
    // ya pasa por `bandejaVigente`, y los contadores del año siguen valiendo (`contadoresDelAnio`).
    this.carrilBandeja = carrilPeticiones({
      cargando: function(si) {
        if (si) self.empezarCarga('bandeja', false);
        else self.terminarCarga(self.cargas.bandeja);
      }
    });
    // Con qué sesión se cargó lo que hay en pantalla, y cuántas veces ha cambiado. Fuera de `data`
    // porque no es estado que se pinte.
    this.sesion = null;
    this.generacion = 0;
    // **Si esta pantalla es la de delante.** Lo pone la entrada en la ruta (`entrar`) y lo quita
    // cualquiera de las tres salidas (`dejarDeMandar`). Es lo que mira `cargar` para no volver a
    // pedir nada desde una pantalla que ya no se ve. Empieza en `false`: hasta el primer
    // `page:beforein` esta pantalla no está delante de nadie.
    this.soyLaDeDelante = false;
    // **El mismo registro, para el formulario de solicitud.** El popup es parte de esta pantalla y
    // el preloader de Framework7 es uno de la **aplicación**, así que un registro propio allí
    // volvería a ser lo que costó las rondas 4 y 11: dos vigilantes del mismo modal que no se
    // hablan, y el que termina antes apaga el reloj del otro. Se le pasa por `:reloj`.
    this.reloj = {
      coger: function(clave) { return self.cogerReloj(clave); },
      soltar: function(testigo) { return self.soltarReloj(testigo); }
    };
    // Qué anulación está en marcha, como testigo propio. NO es `anulandoId`: ese es lo que se
    // pinta, y esto es quién responde de soltarlo (ver `concluirAnulacion`).
    this.anulacionEnCurso = null;
  },
  computed: {
    /**
     * **Lo que esta pantalla pinta es la respuesta de lo que se está mirando, nunca «la última que
     * llegó».** Las tres computadas de aquí abajo son la única puerta por la que los datos del
     * servidor llegan al template, y la comparación la hace `respuestaVigente` para las tres.
     *
     * Es la regla de la que esta ventana lleva varias rondas: el desplegable de años y la barra de
     * pestañas cambian en el acto, pero la respuesta tarda, así que entre lo uno y lo otro la
     * pantalla enseñaba el saldo de otro año o las filas de otra pestaña **con sus botones de
     * Anular, Aprobar y Denegar puestos** (revisiones CODEX de los PR #1733 y #1735). Tapado dato
     * a dato se olvida en el siguiente: aquí hay una sola regla y cada fuente solo tiene que
     * declarar **de qué es** su respuesta, que es lo que el propio servidor devuelve en ella.
     *
     * El resumen del año que se está mirando, o nada. De aquí salen las tarjetas, la barra, las
     * tres listas, los años anteriores, el historial y el pie.
     */
    resumenVigente: function() {
      const r = this.resumen;
      return respuestaVigente(r, r && r.anio, this.anio);
    },
    /**
     * #59 · La bandeja de la pestaña y el año que se están mirando, o nada. Es lo que se le pasa
     * al componente de la bandeja, que cuenta con ello: su prop `datos` dice que la respuesta de
     * la pestaña anterior no es una respuesta de esta.
     */
    bandejaVigente: function() {
      const b = this.bandeja;
      return respuestaVigente(b, b && [b.anio, b.pestana], [this.anio, this.pestana]);
    },
    /**
     * #59 · Los contadores de las cuatro pestañas. Los trae cualquier respuesta de la bandeja, así
     * que **no son de una pestaña**: basta con que sean del año que se está mirando. Por eso están
     * aparte de `bandejaVigente`, que sí lo es: al cambiar de pestaña los cuatro contadores siguen
     * valiendo y vaciarlos sería quitar de la barra números que no han cambiado.
     */
    contadoresDelAnio: function() {
      const b = this.bandeja;
      return respuestaVigente(b, b && b.anio, this.anio);
    },
    /**
     * #59 · Si la bandeja de lo que se está mirando sigue pedida. **No es una propiedad que
     * alguien tenga que apagar**: sale del registro de cargas vivas (`empezarCarga`), así que una
     * respuesta que el carril descarta —la de la pestaña anterior, que ya no es la dueña— no puede
     * apagarla mientras la de ahora sigue en vuelo.
     */
    cargandoBandeja: function() {
      return !!this.cargas.bandeja;
    },
    /**
     * #59 · Las pestañas que se enseñan. «Mis solicitudes» siempre; las cuatro de aprobación solo
     * si el servidor dice que esta persona aprueba a alguien.
     *
     * El contador va **entre paréntesis y solo cuando hay algo**, como la maqueta:
     * «Pendientes (4)», «Confirmadas». Un «(0)» es ruido en una pestaña que está vacía.
     *
     * Y salen de `contadoresDelAnio`, así que al cambiar de **pestaña** se quedan —los cuatro
     * siguen siendo los de este año, y quitarlos sería parpadear por nada— pero al cambiar de
     * **año** desaparecen hasta que conteste el servidor: con los del año anterior puestos, la
     * barra diría «Pendientes (4)» sobre la bandeja de un año que nadie ha leído (§9: dos partes
     * de la misma pantalla no pueden decir lo contrario).
     */
    pestanas: function() {
      const self = this;
      const lista = [{ clave: 'MIS', titulo: self.$t('misVacaciones.misSolicitudes') }];
      if (!self.apruebaAAlguien) return lista;
      const cuenta = self.contadoresDelAnio;
      PESTANAS_DE_APROBACION.forEach(function(p) {
        const nombre = self.$t('bandejaVacaciones.pestana_' + p.clave);
        const cuantas = cuenta && cuenta[p.contador];
        lista.push({
          clave: p.clave,
          titulo: cuantas ? nombre + ' (' + cuantas + ')' : nombre
        });
      });
      return lista;
    },
    saldo: function() {
      return (this.resumenVigente && this.resumenVigente.saldo) || {};
    },
    /**
     * Los años que se pueden elegir. Salen de `anios`, que es lo último que dijo el servidor de
     * **esta persona**, y no del resumen: el resumen es de UN año y se tira en cuanto se elige
     * otro, así que derivar de él el desplegable lo dejaba con un solo año mientras cargaba —no se
     * podía ir a un tercero— y, si esa carga fallaba, reducido para siempre al año que falló, sin
     * forma de volver a uno que sí cargaba (revisión CODEX del PR #1733, ronda 7).
     *
     * La lista no depende del año pedido (`RhMisVacacionesService.aniosDisponibles`: los años de
     * los que tiene solicitudes más el que corre y el siguiente), así que conservarla mientras
     * llega la respuesta nunca enseña un año que no sea suyo.
     *
     * El año de la ventana entra siempre aunque la lista venga vacía: un desplegable sin opciones
     * no enseñaría ni el año que se está mirando.
     */
    aniosDisponibles: function() {
      if (!this.anio || this.anios.indexOf(this.anio) !== -1) return this.anios;
      // Del más reciente al más antiguo, como los manda el servidor.
      return this.anios.concat(this.anio).sort(function(a, b) { return b - a; });
    },
    /**
     * #1119 · Cuántas solicitudes de su equipo esperan respuesta en cada año, `{ 2026: 1, 2027: 2 }`:
     * las pendientes y las anulaciones pedidas, que es lo mismo que cuenta el badge del menú.
     *
     * Salen del `porAnio` de la bandeja, que el servidor cuenta **siempre** para el año en curso
     * y el siguiente, mire el año que mire: por eso se toman de la última respuesta aunque sea de
     * otro año, y no de `bandejaVigente`. Del año que se mira, el servidor lo cuenta con la misma
     * lectura que las pestañas, así que «2027 (2 por resolver)» y «Pendientes (2)» no pueden decir
     * cosas distintas; y el menú suma estos mismos números (§9).
     *
     * Solo para quien aprueba a alguien: a los demás el desplegable les enseña sus años, sin más.
     */
    porResolverDeCadaAnio: function() {
      const cuenta = {};
      const porAnio = this.apruebaAAlguien && this.bandeja && this.bandeja.porAnio;
      ((porAnio && porAnio.anios) || []).forEach(function(a) {
        if (a && a.badge) cuenta[a.anio] = a.badge;
      });
      return cuenta;
    },
    impedimentos: function() {
      return (this.resumenVigente && this.resumenVigente.impedimentos) || [];
    },
    /**
     * Las bolsas de las que salen sus días, **tal como las manda el servidor** y en el orden en
     * que se gastan (`RhBolsaSaldoDTO`, nota técnica de Donchurru del 02/10/2026).
     *
     * Esta lista es la razón por la que esta pantalla ya no hace ninguna cuenta. Antes derivaba el
     * arrastre que cuenta con un `Math.min(arrastreAnterior, consumidos) + arrastreDisponible`:
     * eso era una segunda calculadora del reparto en el navegador, y la primera vez que discrepara
     * de la del servidor nadie sabría cuál mirar. Dirección lo cerró el 02/10/2026 —«el cálculo,
     * el saldo y las validaciones son del servidor; si algo no cuadra, se arregla allí»—.
     */
    bolsas: function() {
      return (this.saldo && this.saldo.bolsas) || [];
    },
    /**
     * Lo que de verdad tiene este año: por cada bolsa, lo gastado más lo que le queda.
     *
     * Lo que una bolsa caducada se llevó sin gastar no entra, y por eso no se suma `dias`: esos
     * días ya no existen y sumarlos dejaría las tarjetas sin cuadrar con la barra, que pinta
     * justamente consumidos + disponibles.
     */
    totales: function() {
      return this.bolsas.reduce(function(total, bolsa) {
        return total + (bolsa.consumidos || 0) + (bolsa.disponibles || 0);
      }, 0);
    },
    /**
     * «23 de convenio + 2 de 2025». Una parte por bolsa y con el año que dice la propia bolsa:
     * el arrastre no tiene por qué ser del año inmediatamente anterior —una bolsa de incapacidad
     * temporal sobrevive a más de un cambio de año (D2, art. 38.3 ET)—, así que restarle uno al
     * año de la ventana la nombraría mal.
     */
    desgloseTotales: function() {
      const self = this;
      const partes = this.bolsas.map(function(bolsa) {
        const dias = (bolsa.consumidos || 0) + (bolsa.disponibles || 0);
        if (bolsa.concepto === 'ARRASTRE') {
          return self.$t('misVacaciones.deAnioAnterior', { dias: dias, anio: bolsa.anioOrigen });
        }
        if (bolsa.concepto === 'EXCEPCIONALES') {
          return self.$t('misVacaciones.deExcepcionales', { dias: dias });
        }
        // El saldo de partida que confirmó Laboral (#682) NO se puede llamar «de convenio»:
        // no son los 23 del convenio sino lo que a esa persona le quedaba a la fecha de
        // corte más lo que ya tenía registrado. Diciendo «de convenio» debajo de un 10, el
        // pie de la tarjeta desmentiría al número de al lado, que es el §9 del CLAUDE.md del
        // orquestador: dos partes de la misma pantalla no pueden decir lo contrario.
        const clave = bolsa.concepto === 'SALDO_DE_PARTIDA' ? 'misVacaciones.deSaldoPartida' : 'misVacaciones.deConvenio';
        // #1158 · La bolsa del año se cuenta desde su cifra de partida —la que declaró Laboral y
        // repite la caja de debajo, o la del convenio— y con lo que la mueve al lado: «23 de saldo
        // de partida − 2 de ajuste de RRHH». Con el número a secas, la tarjeta decía 21 y la caja
        // 23, y nada en la pantalla explicaba los dos días (caso MVG).
        const composicion = self.composicionBolsaDelAnio(dias);
        if (composicion) {
          return [self.$t(clave, { dias: composicion.partida })]
            .concat(composicion.movimientos.map(self.textoMovimiento)).join(' ');
        }
        return self.$t(clave, { dias: dias });
      }).filter(function(parte) { return parte; });
      // Y por lo mismo tampoco se avisa de prorrateo con un saldo confirmado: ese número no
      // sale de ningún prorrateo, y el servidor ya manda `prorrateado` a false en ese caso.
      if (this.saldo.prorrateado) {
        partes.push(this.$t('misVacaciones.prorrateado'));
      }
      return partes.join(' + ');
    },
    /**
     * **De dónde sale el número cuando lo confirmó Laboral** (#682): el saldo que le quedaba a
     * esa persona a una fecha, ya descontado todo lo disfrutado y lo que tuviera puesto a
     * futuro ese día.
     *
     * Se dice UNA vez y en un sitio, igual que los días excepcionales, porque es la respuesta
     * a «¿por qué tengo 6 y no 23?». Sin decirlo, la pantalla enseña una cifra que no se
     * parece al convenio y no hay dónde mirar; repitiéndolo en cada tarjeta, uno de los sitios
     * se queda viejo (§9).
     *
     * El dato es del servidor y aquí no se recalcula nada: solo se decide si se pinta.
     */
    textoSaldoPartida: function() {
      const s = this.saldo;
      if (s.saldoDePartidaDeclarado === null || s.saldoDePartidaDeclarado === undefined) return '';
      return this.$t('misVacaciones.saldoPartidaConfirmado', {
        dias: s.saldoDePartidaDeclarado,
        fecha: s.saldoDePartidaCorte ? this.fecha(s.saldoDePartidaCorte) : ''
      });
    },
    /**
     * La bolsa de días excepcionales de RRHH, si la tiene. Se enseña aparte porque es la única que
     * puede llevar una fecha distinta a la de todos los demás: desde la decisión de dirección del
     * 02/10/2026 caducan **cuando los ordinarios**, el 31 de marzo del año siguiente, y la única
     * excepción que queda es la de incapacidad temporal, que por ley va más allá.
     *
     * #69 · Y las de incapacidad temporal **no entran aquí**, porque tienen su propia línea justo
     * debajo —la que pidió dirección el 21/09/2026— y van en el MISMO sitio de la respuesta: un
     * ajuste de tipo IT llega como una bolsa `EXCEPCIONALES` más, así que sin este filtro los
     * mismos días se contaban dos veces en dos frases distintas de la misma pantalla.
     */
    bolsaExcepcionales: function() {
      return this.bolsas.filter(function(bolsa) {
        return bolsa.concepto === 'EXCEPCIONALES' && !bolsa.porIncapacidadTemporal
          && ((bolsa.consumidos || 0) + (bolsa.disponibles || 0)) > 0;
      })[0] || null;
    },
    /**
     * El texto de los días excepcionales. Dice hasta cuándo valen; lo de «valen más tiempo» ya no
     * puede salir por aquí, porque la única bolsa que vencía más tarde era la de incapacidad
     * temporal y esa tiene línea propia (ver {@link bolsaExcepcionales}).
     */
    textoExcepcionales: function() {
      const b = this.bolsaExcepcionales;
      return this.$t('misVacaciones.excepcionales', {
        dias: (b.consumidos || 0) + (b.disponibles || 0),
        hasta: b.caduca ? this.fecha(b.caduca) : this.$t('misVacaciones.sinCaducidad')
      });
    },
    /**
     * #69 · «De los disponibles, N son por incapacidad temporal y valen hasta <fecha>», que es la
     * línea que pidió dirección el 21/09/2026 con lo que falta para que no se lea como un sumando.
     *
     * Lleva la fecha porque es el dato que los distingue del resto del saldo: estos valen 18 meses
     * desde el final del año (art. 38.3 del Estatuto) y no hasta el 31 de marzo del siguiente (D2).
     * La fecha la calcula el servidor; aquí solo se escribe.
     *
     * Y dice «de los disponibles» porque **lo están**: el servidor los saca del mismo desglose con
     * el que compone `saldo` (`RhSaldoVacacionesService`), así que son una parte del número grande
     * y no otros días más. Escrito como una cifra suelta, «Disponibles: 23» y «2 días por
     * incapacidad temporal» se leían como 25, que es el defecto que dirección registró el
     * 05/10/2026: dos partes de la misma pantalla diciendo cosas distintas.
     *
     * **Y cuando hay más de un vencimiento, una línea por vencimiento.** Estos días pueden venir
     * de bolsas de años distintos a la vez —los de 2025 caducan en junio de 2027 y los de 2026 en
     * junio de 2028, y en 2027 conviven—, así que una sola frase con el total y una fecha diría
     * que todos valen hasta la última, que es la misma clase de defecto: la pantalla afirmando
     * algo que no es. El reparto lo manda el servidor en `bolsas`; aquí solo se escribe.
     */
    bolsasIncapacidadTemporal: function() {
      return this.bolsas.filter(function(bolsa) {
        return bolsa.porIncapacidadTemporal && (bolsa.disponibles || 0) > 0;
      });
    },
    lineasIncapacidadTemporal: function() {
      const self = this;
      const s = this.saldo;
      const bolsas = this.bolsasIncapacidadTemporal;
      const hasta = function(caduca) {
        return caduca ? self.fecha(caduca) : self.$t('misVacaciones.sinCaducidad');
      };
      // Lo normal: una sola bolsa. Se escribe exactamente la frase que pidió dirección.
      if (bolsas.length < 2) {
        if (!s.diasIncapacidadTemporal) {
          return [];
        }
        return [this.$t('anularVacaciones.diasPorIncapacidad', {
          dias: bolsas.length === 1 ? bolsas[0].disponibles : s.diasIncapacidadTemporal,
          hasta: hasta(bolsas.length === 1 ? bolsas[0].caduca : s.incapacidadTemporalCaduca)
        })];
      }
      const lineas = [this.$t('anularVacaciones.diasPorIncapacidadVarias', {
        dias: bolsas.reduce(function(total, bolsa) { return total + bolsa.disponibles; }, 0)
      })];
      bolsas.forEach(function(bolsa) {
        lineas.push(self.$t('anularVacaciones.diasPorIncapacidadBolsa', {
          dias: bolsa.disponibles, hasta: hasta(bolsa.caduca)
        }));
      });
      return lineas;
    },
    /** Las tres listas de la maqueta, repartidas por el estado que manda el servidor. */
    grupos: function() {
      const solicitudes = (this.resumenVigente && this.resumenVigente.solicitudes) || [];
      const self = this;
      return [
        { clave: 'solicitadas', estados: ['PENDIENTE'] },
        { clave: 'confirmadas', estados: ['CONFIRMADA'] },
        { clave: 'disfrutadas', estados: ['REALIZADA'] }
      ].map(function(g) {
        return {
          clave: g.clave,
          titulo: self.$t('misVacaciones.lista_' + g.clave, { anio: self.anio }),
          vacio: self.$t('misVacaciones.listaVacia_' + g.clave),
          solicitudes: solicitudes.filter(function(s) { return g.estados.indexOf(s.estado) >= 0; })
        };
      });
    },
    /**
     * **El disponible tal como lo pinta la barra: nunca por debajo de cero** (#70).
     *
     * Con el saldo en negativo —que es lo que deja registrar vacaciones en nombre de otra persona
     * sin bloquear por saldo— la barra no tiene ningún tramo disponible que pintar: lo que hay es
     * una deuda, y el navegador ignora un ancho negativo, así que el tramo desaparecía igual pero
     * de paso se llevaba el total por delante. Con 25 días consumidos y saldo −2 el total salía 23
     * y el tramo consumido medía `25/23`, o sea más del 100 %: la barra se pasaba de largo y los
     * tres colores quedaban en proporciones que no eran las suyas.
     *
     * Cero, y no el valor absoluto: la barra mide lo que hay, y de la deuda ya responden el número
     * en rojo de la tarjeta y su pie (`pieDisponibles`).
     */
    disponiblesEnLaBarra: function() {
      return Math.max(0, this.saldo.saldo || 0);
    },
    /**
     * Y el total con el que se reparte: los tres consumos más el disponible **de la barra**. Si no
     * hay nada que pintar es 0 y los tramos se quedan a cero (ver `ancho`).
     */
    totalDeLaBarra: function() {
      const s = this.saldo;
      return (s.disfrutados || 0) + (s.confirmados || 0) + (s.solicitados || 0)
        + this.disponiblesEnLaBarra;
    },
    tituloBarra: function() {
      const s = this.saldo;
      return this.$t('misVacaciones.tituloBarra', {
        disfrutadas: s.disfrutados || 0,
        confirmadas: s.confirmados || 0,
        solicitadas: s.solicitados || 0,
        disponibles: s.saldo || 0
      });
    },
    /**
     * El pie de la tarjeta «Disponibles». **«para solicitar» solo cuando se puede solicitar.**
     *
     * Con el botón apagado, la tarjeta decía «Disponibles 23 · para solicitar» mientras el aviso
     * de al lado decía que su saldo estaba pendiente de RRHH: dos partes de la misma pantalla
     * diciendo lo contrario, que es el ejemplo literal del §9 del `CLAUDE.md` del orquestador.
     * Lo encontró dirección el 10/10/2026 probando esta ventana.
     *
     * El número no se toca —el saldo es del servidor (#57)— y el motivo tampoco se repite aquí:
     * se dice UNA vez, junto al botón, que es donde se actúa.
     */
    pieDisponibles: function() {
      // Y si el saldo es NEGATIVO manda eso, antes que las otras dos (#70): un «para solicitar»
      // debajo de un −2 es el mismo defecto de §9 que esta función vino a cerrar, contado al
      // revés. El número ya está en rojo; el pie dice por qué.
      if (this.saldoNegativo) return this.$t('misVacaciones.pieDisponiblesNegativo');
      return this.resumenVigente && this.resumenVigente.puedeSolicitar
        ? this.$t('misVacaciones.pieDisponibles')
        : this.$t('misVacaciones.pieDisponiblesBloqueado');
    },
    /**
     * Si el saldo está por debajo de cero. Pasa desde la #70: registrar vacaciones en nombre de
     * otra persona no bloquea por saldo insuficiente —se anota algo que ya ocurrió—, solo avisa, y
     * quien acepta deja a esa persona en negativo.
     *
     * El número es del servidor y aquí no se recalcula nada: solo se decide cómo se pinta.
     */
    saldoNegativo: function() {
      return (this.saldo.saldo || 0) < 0;
    },
    /**
     * El pie de la ventana: quién le aprueba las vacaciones.
     *
     * **No dice nada cuando no tiene aprobador**, aunque antes decía «No tienes aprobador
     * asignado. Avisa a RRHH.». Ese caso ya lo cuenta el impedimento de al lado del botón, con
     * quién lo desbloquea y dónde, y contarlo también aquí es el defecto de §9 por duplicado: la
     * causa se dice una vez, porque dos sitios que la explican por su cuenta acaban con uno de los
     * dos viejo. Son exactamente el mismo caso —el servidor saca `aprobador` del mismo sitio del
     * que decide el impedimento—, así que no se pierde nada.
     */
    pieAprobador: function() {
      const r = this.resumenVigente;
      if (!r) return '';
      if (r.autoconfirma) return this.$t('misVacaciones.pieAutoconfirma');
      const quien = r.aprobadorNombre || r.aprobador;
      return quien ? this.$t('misVacaciones.pieAprobador', { quien: quien }) : '';
    }
  },
  methods: {
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    entrar: function() {
      // Lo primero de todo: con quién se decide qué se enseña. La ruta lleva `keepAlive`, así que
      // esta instancia es la que montó la PRIMERA resolución y sus `props` son los de entonces;
      // entrar con otra cuenta dejaba el botón de registrar según el permiso de la anterior (ver
      // `permisosDeLaRuta`). Aquí ya ha resuelto la barrera de la ruta, así que `permisos.js`
      // contesta con el usuario de quien está delante.
      this.revisarPermisosDeLaRuta();
      // Antes de pedir nada: si ha entrado otra persona, lo que hay en pantalla es de la anterior.
      this.comprobarSesion();
      // Y volver a ser la de delante se declara DESPUÉS de preguntarlo, no antes: si la sesión ha
      // cambiado, `comprobarSesion` pasa por `dejarDeMandar`, que es justo quien lo quita.
      this.soyLaDeDelante = true;
      // #59 · el botón VACACIONES del calendario de otra persona entra con `?pestana=PENDIENTES`:
      // quien va a resolver llega directamente a lo que tiene que resolver. Y sin `?pestana=` se
      // vuelve a «Mis solicitudes» EXPLÍCITAMENTE, no se deja la de antes: con `keepAlive` la
      // pantalla no se recrea, y volver desde el menú lateral devolvía al aprobador a la bandeja
      // que estaba mirando la última vez. Lo que pida la URL tiene que ser una pestaña que exista:
      // el porqué, en `pestanaPedida`.
      this.pestana = pestanaPedida(this.$f7route && this.$f7route.query);
      this.cargar();
    },
    /**
     * Con quién se cargó lo que hay en pantalla. **Lo dice el adaptador**
     * (`RhSolicitudesVacaciones.sesionActual`), que es el mismo sello al que van atadas las
     * escrituras del módulo: la pantalla y sus POST tienen que medir la misma sesión, porque dos
     * vigilantes que no se hablan es exactamente lo que costó cuatro rondas en el PR #1759.
     *
     * Antes se componía aquí con el usuario y el token. El token sobra y además estorba: en
     * Cordova la aplicación renueva la sesión sola con las credenciales guardadas, y eso daba un
     * sello nuevo —y una carga descartada— sin que hubiera cambiado quién está delante.
     */
    sesionActual: function() {
      return RhSolicitudesVacaciones.sesionActual();
    },
    /**
     * **Si quien está delante ya no es quien cargó lo que se ve.** Lo pregunta la entrada en la
     * ruta (`entrar`), que es cuando puede haber pasado sin que nadie avisara —entrar con otra
     * cuenta llega por el aviso del módulo, pero la primera entrada no tiene con qué comparar—.
     *
     * Si ha cambiado, lo de la cuenta anterior se tira por el mismo sitio que cualquier otra
     * salida: `dejarDeMandar`. Aquí solo se decide **si** ha cambiado; lo que se tira, allí.
     */
    comprobarSesion: function() {
      const sesion = this.sesionActual();
      if (sesion === this.sesion) return;
      this.sesion = sesion;
      this.dejarDeMandar(MOTIVO_CIERRE_DE_SESION);
    },
    /**
     * **Esta pantalla deja de ser la de delante: nada de lo que tenga en vuelo vuelve a decidir.**
     *
     * Las tres salidas —cerrar sesión, salir de la pantalla y destruirse— entran por aquí, y
     * quién las conoce es `soloMandaLaDeDelante`, que es donde están enumeradas. Cada una de ellas
     * costó una ronda de revisión por separado (rondas 6, 7 y 8 del PR #1733) porque el arreglo
     * vivía en el hook de la salida señalada: la ruta es `keepAlive`, así que ni salir ni cerrar
     * sesión destruyen nada y lo pedido seguía siendo la petición vigente de su carril.
     *
     * Lo que se hace es siempre lo mismo, venga la salida de donde venga:
     *
     *  - **el carril recibe un sello nuevo**, que es lo que invalida de golpe lo que esté en vuelo:
     *    ninguna de esas respuestas puede ya pintar, ni declarar un fallo —ni abrir su aviso encima
     *    de la pantalla siguiente—, ni su 403 (ver `carrilPeticiones`);
     *  - **se suelta el reloj**, que es un modal de la **aplicación**: un dueño que ya no mira
     *    dejaría el «cargando» puesto encima de lo que haya delante, y su respuesta tardía lo
     *    apagaría cuando ya fuera de otra pantalla (ver `cogerReloj`);
     *  - **se abre el cerrojo de la anulación**, porque su respuesta ya no va a abrirlo:
     *    `concluirAnulacion` solo suelta lo que sigue siendo suyo, así que dejarlo puesto era una
     *    lista que no deja volver a anular nada;
     *  - **y el formulario que contiene deja de mandar sobre lo suyo**: el popup no recibe el aviso
     *    de salida por su cuenta —es de la página— y sus dos lecturas van por carriles propios.
     *
     * Y además **deja de ser la pantalla de delante**, que no es lo mismo que invalidar lo que está
     * en vuelo: lo que queda vivo después de una escritura es su CONTINUACIÓN —el `then` de la
     * anulación, el `enviada` del formulario—, que no está en ningún carril y lo que hace es
     * **empezar una lectura nueva**. Invalidar no la alcanza; lo que la corta es que `cargar` no
     * pida nada desde una pantalla que ya no se ve (revisión CODEX del PR #1733, ronda 9 del 15/10).
     *
     * Lo que **solo** se hace al cambiar de sesión es tirar lo que se está viendo: el saldo, el año
     * y las solicitudes son datos personales de la cuenta anterior, y con `keepAlive` quien entrara
     * después los vería durante toda la primera carga, además de pedirse el año que había elegido
     * aquella (ronda 5). Al salir de la pantalla no se tira: no hay nada que ocultar a nadie, la
     * siguiente entrada recarga igual, y vaciarla aquí sería un parpadeo al volver.
     *
     * `enviando` no se toca por ninguna de las tres: el cerrojo del botón de enviar lo suelta el
     * envío que lo cogió (`concluirEnvio`), y soltarlo desde fuera encendía un botón que el
     * adaptador rechaza como repetido, o sea un botón que no hace nada (ronda 6 y §9 del
     * `CLAUDE.md` del orquestador).
     */
    dejarDeMandar: function(motivo) {
      this.soyLaDeDelante = false;
      this.generacion += 1;
      this.carril.sesion(this.generacion);
      this.carrilBandeja.sesion(this.generacion);
      this.anulandoId = null;
      this.anulacionEnCurso = null;
      // Aquí se van las dos señales de carga a la vez —el reloj y el «cargando» de la bandeja—,
      // porque las dos salen del mismo registro (`empezarCarga`).
      this.olvidarTodasLasCargas();
      // A sus popups les llega la salida por aquí y solo la salida: el cierre de sesión y su propia
      // destrucción los conocen ellos (los tienen por el mismo sitio, `soloMandaLaDeDelante`), y
      // avisarles dos veces de lo mismo no haría daño pero escondería quién responde de cada aviso.
      // Van TODOS los de `POPUPS`, no el que se recuerde: el que falte se queda con sus lecturas en
      // vuelo sin que falle nada.
      if (motivo === MOTIVO_SALIDA_DE_PANTALLA) {
        const self = this;
        POPUPS.forEach(function(popup) {
          const hijo = self.$refs[popup.ref];
          // Puede no estar montado: el de registrar en nombre de otra persona solo existe para
          // quien puede usarlo (`puedeRegistrarEnNombreDeAhora`).
          if (hijo) avisar(hijo, motivo);
        });
      }
      if (motivo !== MOTIVO_CIERRE_DE_SESION) return;
      this.resumen = null;
      // El año se deja en `null` a propósito: sin año el servidor devuelve el que corre en Madrid,
      // que es lo que la pantalla enseña en la primera carga de cualquiera.
      this.anio = null;
      // Los años también: son los de las solicitudes de la cuenta anterior, y el desplegable los
      // enseñaría antes de que conteste la primera carga de la nueva.
      this.anios = [];
      this.error403 = false;
      // #59 · y la bandeja del equipo, que son datos personales de las personas que aprobaba la
      // cuenta anterior: las filas, los contadores, si aprobaba a alguien y la pestaña abierta.
      this.bandeja = null;
      this.apruebaAAlguien = false;
      this.pestana = 'MIS';
      // Y los popups se cierran, TODOS: no se destruyen al cerrarlos, así que lo que llevaran
      // escrito —los tramos, el comentario, la persona elegida, el motivo— era de la cuenta
      // anterior y con `keepAlive` seguiría abierto encima del login o de la cuenta siguiente. Al
      // volver a abrirlos, su `abrir()` los empieza de cero.
      const self = this;
      POPUPS.forEach(function(popup) { self[popup.abierto] = popup.cerrado; });
    },
    /**
     * **Al cambiar de año se tira lo del año anterior.** El título y el desplegable cambian al
     * momento, pero el saldo, las listas y las tablas siguen siendo del año de antes hasta que
     * conteste el servidor: en una conexión lenta se leía «Solicitadas sin aprobar de 2025» con las
     * cifras de 2026, que es la misma clase de defecto que un botón que no dice nada (§9 del
     * `CLAUDE.md` del orquestador: dos partes de la misma pantalla no pueden decir lo contrario).
     *
     * Dirección lo decidió el 09/10/2026 para esta pantalla, y la #472 lo pasó a la pieza común:
     * **quien vacía es el carril**, al lanzar la carga de un año distinto del pintado (su
     * `vaciar`, en `created`), y el «cargando» lo apaga él con la respuesta vigente. Aquí solo se
     * cambia el año y se pide: no hay nada de lo que acordarse.
     */
    /** #1119 · El texto de cada año del desplegable: el año y, si le espera algo, cuánto. */
    etiquetaAnio: function(anio) {
      const n = this.porResolverDeCadaAnio[anio];
      return n ? this.$t('misVacaciones.anioConPendientes', { anio: anio, n: n }) : anio;
    },
    cambiarAnio: function(evento) {
      this.anio = parseInt(evento.target.value, 10);
      this.cargar();
    },
    /**
     * **Una carga de esta pantalla empieza aquí y la cierra solo ella: la nueva de su clave
     * releva a la anterior.** Es el único registro de «qué está pedido» que tiene la ventana, y de
     * él salen sus dos señales de que algo se está cargando: el reloj de la aplicación —el
     * preloader de Framework7, que lo piden las cargas que tapan la pantalla entera— y el
     * «cargando» de la bandeja, que es de la tabla y no tapa nada (`cargandoBandeja`).
     *
     * Los dos aquí y no cada uno por su cuenta: la bandeja llevaba su propio `cargandoBandeja`
     * apagado en el `then` del carril, y ese `then` se cumple **también para las respuestas que el
     * carril descarta**, así que dos cambios rápidos de pestaña lo apagaban con la petición de la
     * pestaña nueva todavía en vuelo —y con ella los botones Aprobar y Denegar de las filas de
     * antes volvían a encenderse— (revisión CODEX del PR #1735, ronda 6). Dos vigilantes del mismo
     * estado que no se hablan es lo que ya costó las rondas 4 y 11 del PR #1733 con el reloj.
     *
     * La `clave` nombra la carga: `resumen` para la lectura de la ventana, `bandeja` para la del
     * equipo y `anular|<id>` para cada anulación.
     *
     * `conReloj` dice si además enciende el reloj de la aplicación. La bandeja no: un fallo suyo no
     * tumba la pantalla —«Mis solicitudes» se sigue viendo— y tapar la ventana entera al cambiar de
     * pestaña escondería justo lo que se está mirando.
     *
     * **Y el reloj no se enciende aquí: se le pide a `esperaGlobal`**, que es el dueño del modal
     * para toda la aplicación (decisión técnica de Donchurru del 17/10/2026). Esta pantalla
     * encendía y apagaba el preloader por su cuenta mientras las acciones lo pedían a esa cuenta
     * común, así que el `then` final de una acción —que suelta lo suyo— se encontraba la cuenta a
     * cero y apagaba el modal con la recarga que ella misma acababa de arrancar todavía en vuelo:
     * volvían a la vista el saldo y las solicitudes de antes como si ya fueran definitivos
     * (revisión CODEX del PR #1735, ronda 7). Con el testigo en la cuenta común, el indicador no se
     * apaga mientras quede una espera viva, venga de la pantalla o de la acción.
     *
     * Lo que sigue siendo de la pantalla es **cuándo** se pide y cuándo se suelta: el registro es
     * suyo, el relevo por clave es suyo y las tres salidas son suyas.
     */
    empezarCarga: function(clave, conReloj) {
      const cargas = Object.assign({}, this.cargas);
      const relevada = cargas[clave];
      const testigo = {
        clave: clave,
        // El testigo de la espera de ESTA carga, o `null` si no pide el reloj. Es lo único con lo
        // que se puede soltar, y soltarlo dos veces no hace nada.
        espera: conReloj ? pedirEspera(this.$f7) : null
      };
      // **La carga nueva de una clave releva a la anterior**, y relevar es soltar lo que tenía
      // pedido: si no, la espera de la relevada se queda en la cuenta común para siempre —su
      // respuesta ya no es dueña de nada y no vuelve a pasar por aquí— y el indicador no se apaga
      // nunca. Soltar la suya no toca el de nadie más, que es para lo que están los testigos.
      //
      // Y se suelta DESPUÉS de pedir la nueva, nunca antes: al revés, si la relevada era la única
      // espera viva la cuenta bajaba a cero, el indicador se apagaba y se volvía a encender en el
      // mismo instante. Un parpadeo del «espere» en cada cambio de año, y un hueco en el que la
      // pantalla parecía terminada con los datos del año anterior todavía puestos. El contrato es
      // que el indicador no se apaga mientras quede una espera vigente, y durante el relevo
      // siempre la hay: la que entra.
      if (relevada) soltarEspera(relevada.espera);
      // El registro se reemplaza entero y no se toca por dentro: una clave nueva añadida a mano no
      // la ve el repintado de Vue 2, y de este registro cuelga ahora el «cargando» de la bandeja.
      cargas[clave] = testigo;
      this.cargas = cargas;
      return testigo;
    },
    /**
     * Cierra ESA carga y suelta la espera que cogió. Devuelve si el testigo seguía siendo el dueño:
     * una respuesta de la sesión anterior, o de una petición ya relevada, no cierra nada y tampoco
     * toca lo que haya empezado la de ahora —ni su espera, que es de otro testigo—.
     */
    terminarCarga: function(testigo) {
      if (!testigo || this.cargas[testigo.clave] !== testigo) return false;
      const cargas = Object.assign({}, this.cargas);
      delete cargas[testigo.clave];
      this.cargas = cargas;
      soltarEspera(testigo.espera);
      return true;
    },
    /**
     * **El «cargando» se coge por cuenta de UNA petición de esta pantalla, y lo suelta solo ella.**
     *
     * El preloader de Framework7 es un modal de la aplicación y **no se cuenta**
     * (`framework7/components/preloader/preloader.js`: `show()` no hace nada si ya hay uno y
     * `hide()` quita el que haya, sea de quien sea). Mientras lo encendía y lo apagaba cada
     * petición a pelo, cualquiera que terminase apagaba el de las demás: el `catch` de una
     * anulación que falla apagaba el de la recarga que seguía en vuelo —y al cambiar de año, con el
     * resumen ya vacío, la pantalla se quedaba en blanco y sin ninguna señal de que estaba
     * cargando— (revisión CODEX del PR #1733, ronda 4; decisión técnica de Donchurru del
     * 11/10/2026).
     *
     * Ni la sesión ni `anulandoId` sirven para decidir eso: las dos dicen **cómo está la pantalla
     * ahora**, no de quién es el reloj que hay puesto. Lo que lo acredita es este testigo, que es
     * del que lo cogió.
     *
     * La `clave` nombra la petición: `resumen` para la lectura de la ventana y `anular|<id>` para
     * cada anulación. Una petición nueva con la misma clave **releva** a la anterior —el carril ya
     * ha descartado la lectura vieja, así que su respuesta no pinta—, y por eso el testigo relevado
     * deja de ser dueño en el momento de lanzar la siguiente y no cuando contesta: si no, dos
     * cambios de año seguidos dejaban el reloj puesto hasta que contestara la primera, que es la
     * que ya no importa.
     *
     * **Y el reloj que se coge es el de la aplicación**, el de `esperaGlobal`: esta pantalla y las
     * acciones de aprobar, denegar y anular piden al mismo sitio, así que ninguna puede apagar el
     * de la otra. Lo que queda en esta pantalla es el registro de qué tiene pedido ella y el relevo
     * por clave (ver `empezarCarga`). Las otras nueve pantallas que encienden el preloader a pelo
     * siguen como estaban: cambiarlas es la tarea aparte que ya está decidida, y aquí lo que se
     * cierra son los consumidores de este flujo.
     */
    cogerReloj: function(clave) {
      return this.empezarCarga(clave, true);
    },
    /**
     * Suelta el reloj de ESA petición, y lo apaga solo si no queda ninguna otra viva de esta
     * pantalla. Devuelve si el testigo seguía siendo el dueño: una respuesta de la sesión anterior,
     * o de una lectura ya relevada, no suelta nada y tampoco toca lo que haya cogido la de ahora.
     */
    soltarReloj: function(testigo) {
      return this.terminarCarga(testigo);
    },
    /**
     * Lo que estuviera pedido deja de estarlo, por cualquiera de las tres salidas (`dejarDeMandar`):
     * ni el reloj ni el «cargando» de la bandeja se quedan puestos sobre una pantalla que ya no
     * manda, y la respuesta que llegue tarde no es dueña de nada.
     *
     * Se suelta **espera por espera**, cada una con su testigo, y no apagando el modal de un
     * `hide()`: salir de aquí no puede quitarle el indicador a una acción que sigue en vuelo —el
     * aviso de anulada llega igual, porque es de la sesión y no de la pantalla— ni a la primera
     * carga de la cuenta que acaba de entrar. Si las de esta pantalla eran las únicas, al soltar la
     * última se apaga solo.
     */
    olvidarTodasLasCargas: function() {
      const cargas = this.cargas;
      const claves = Object.keys(cargas);
      if (claves.length === 0) return;
      claves.forEach(function(clave) { soltarEspera(cargas[clave].espera); });
      this.cargas = {};
    },
    /**
     * **Una anulación termina en un solo sitio**, por los dos caminos: suelta el cerrojo que apaga
     * los botones de la lista y el reloj que cogió, y nada más.
     *
     * Y son **dos propiedades distintas, con dos dueños distintos**, que es lo que se aprendió en
     * el formulario de solicitud (ronda 1 de esta tanda, r4241397173):
     *
     *  - el **cerrojo** (`anulandoId`) es de ESTA anulación y lo suelta ella, sin mirar la sesión
     *    ni quién tiene el reloj. Colgándolo de otra condición se queda puesto cuando esa condición
     *    no se cumple, y entonces la lista no vuelve a dejar anular nada;
     *  - el **reloj** es de la aplicación y lo suelta el testigo: si ya no es suyo —lo relevó una
     *    lectura, o se tiró el registro al cambiar de sesión— no lo toca.
     *
     * El testigo propio es lo que impide el fallo simétrico: la respuesta de una anulación de la
     * cuenta anterior no suelta el cerrojo de la anulación que haya empezado quien entra después
     * (`comprobarSesion` se lo quita).
     */
    concluirAnulacion: function(anulacion) {
      this.soltarReloj(anulacion.reloj);
      if (this.anulacionEnCurso !== anulacion) return;
      this.anulacionEnCurso = null;
      this.anulandoId = null;
    },
    /**
     * El «cargando» se apaga **solo con la respuesta que manda**, no con la primera que llegue.
     * Apagándolo en el `then` de cualquiera, dos cambios de año seguidos dejaban la pantalla sin
     * reloj mientras la segunda petición seguía en vuelo, y lo que había delante parecía ya
     * definitivo (revisión CODEX del PR #1733, ronda 6). Desde la #472 eso lo decide el carril: su
     * `cargando` coge el reloj al lanzar y lo suelta cuando ya no queda ninguna petición que pueda
     * escribir (ver `created`), así que ni `exito` ni `fallo` tienen que acordarse de nada.
     *
     * Y lo que se suelta es **el testigo de esta lectura** (`cogerReloj`), no un `hide()` suelto:
     * mientras quede una anulación en vuelo de esta pantalla, el reloj sigue puesto aunque la
     * lectura termine, y al revés.
     *
     * **Y desde una pantalla que ya no está delante no se pide nada.** Aquí pasan las cuatro
     * recargas de la ventana —entrar, «Recargar», cambiar de año y lo que sigue a una escritura—,
     * así que es el único sitio donde hace falta decirlo. La ruta es `keepAlive`: salir de la
     * pantalla no la destruye, y la continuación de una anulación o de un envío seguía llamando
     * aquí desde una pantalla oculta, encendiendo otra vez el reloj —que es un modal de la
     * **aplicación**— encima de la pantalla de destino y abriendo allí el aviso si esa recarga
     * fallaba (revisión CODEX del PR #1733, ronda 9 del 15/10).
     *
     * No hay nada que aplazar: la siguiente entrada en la ruta recarga siempre (`entrar`), así que
     * lo que se deja sin pedir ahora se pide al volver, que es cuando alguien lo va a mirar.
     */
    /**
     * #59 · Cambiar de pestaña. La bandeja de la nueva se pide entera: sus filas, y de paso los
     * cuatro contadores, que una resolución en otra pestaña puede haber movido.
     */
    cambiarPestana: function(clave) {
      if (this.pestana === clave) return;
      this.pestana = clave;
      if (clave !== 'MIS') this.cargarBandeja();
    },
    /**
     * #59 · Lo que hay que rehacer después de aprobar o denegar desde la bandeja.
     *
     * Se vuelve a pedir **todo y entero**, y no se toca la fila en memoria: una resolución cambia
     * el saldo de esa persona, los cuatro contadores y lo que ve la pestaña de al lado, así que
     * repintar solo la fila dejaría el resto mintiendo. Y el resumen propio también, porque quien
     * aprueba puede ser a la vez quien tiene solicitudes suyas en la otra pestaña.
     */
    resuelta: function() {
      this.cargar();
    },
    /**
     * #59 · La bandeja del aprobador: las filas de la pestaña abierta y los contadores de las
     * cuatro, en una sola petición.
     *
     * Un fallo aquí **no tumba la pantalla**: «Mis solicitudes» es de todo el mundo y tiene que
     * seguir viéndose aunque lo del equipo no se haya podido leer. Y lo que se pinta nunca es la
     * respuesta de antes: de eso responde `bandejaVigente`, no este método.
     *
     * La clave es [año, pestaña] y se pregunta al llegar la respuesta, no al lanzarla: así una
     * petición lenta de la pestaña anterior no pinta sus filas bajo el título de la nueva aunque
     * vuelva la última (ver `carrilPeticiones`).
     *
     * **El año solo desempata si se ha pedido uno.** En la primera carga no hay año todavía —lo
     * elige el servidor y lo trae en la respuesta—, así que comparar ese `null` con el año que el
     * resumen acaba de fijar descartaba la única respuesta que iba a llegar: el aprobador se
     * quedaba sin pestañas y sin contadores hasta pulsar «Recargar» (revisión CODEX del PR #1735,
     * ronda 6). Dejarla pasar no pinta nada de otro año: de qué es la respuesta lo dice ella misma
     * y lo comprueba `bandejaVigente`.
     */
    cargarBandeja: function() {
      const self = this;
      const pestanaDeLaBandeja = function() {
        return self.pestana === 'MIS' ? 'PENDIENTES' : self.pestana;
      };
      const pedida = [self.anio, pestanaDeLaBandeja()];
      const deLaBandeja = function() {
        return [pedida[0] == null ? null : self.anio, pestanaDeLaBandeja()];
      };
      self.carrilBandeja.lanzar(RhBandejaVacaciones.bandeja(pedida[1], pedida[0]), {
        clave: pedida,
        deAhora: deLaBandeja,
        exito: function(res) {
          self.bandeja = res;
          // Si aprueba a alguien lo dice SIEMPRE una respuesta buena, en los dos sentidos: así la
          // barra de pestañas aparece cuando la hay y desaparece cuando deja de haberla.
          self.apruebaAAlguien = !!(res && res.apruebaAAlguien);
          // Quien no aprueba a nadie no tiene esas pestañas, así que si venía pedida una —por el
          // `?pestana=` de la URL— se vuelve a «Mis solicitudes» en vez de dejar en pantalla una
          // pestaña que la barra de arriba ya no enseña.
          if (res && !res.apruebaAAlguien && self.pestana !== 'MIS') self.pestana = 'MIS';
        },
        fallo: function(e) {
          // Un 403 aquí no es un error que enseñar: es que esta persona no aprueba a nadie, y la
          // pantalla ya no le enseña las pestañas. Se ve al abrir una URL guardada con
          // `?pestana=PENDIENTES` sin serlo. Lo demás sí se dice.
          if (self.es403(e)) {
            self.apruebaAAlguien = false;
            if (self.pestana !== 'MIS') self.pestana = 'MIS';
            return;
          }
          self.$f7.dialog.alert(mensajeDelServidor(e, self.$t('bandejaVacaciones.errorCargando')));
        },
        limpiar: function() {
          // Lo mismo que hace `cargar` con el resumen, y SOLO eso. Lo que no se toca es la barra
          // de pestañas: un error de red al cambiar de pestaña dejaba al aprobador sin ellas, y
          // con `keepAlive` sin forma de recuperarlas. Que aprueba a alguien lo retira el 403, que
          // es quien lo sabe.
          self.bandeja = null;
        }
      });
    },
    cargar: function() {
      const self = this;
      if (!self.soyLaDeDelante) return;
      // #59 · la bandeja se pide siempre, aunque se abra en «Mis solicitudes»: de ella salen la
      // barra de pestañas y sus contadores, que hay que saber antes de que nadie pulse nada.
      self.cargarBandeja();
      const app = self.$f7;
      // El «No autorizado» de un intento anterior se va al lanzar, no al contestar: es el veredicto
      // de una petición que ya no es la vigente, y la ruta lleva `keepAlive`, así que la pantalla no
      // se recrea y se quedaría puesto todo lo que tarde «Recargar» o el cambio de año. Mientras
      // carga lo que hay que ver es el reloj, no una respuesta de antes. Es el mismo criterio que el
      // `errorCalculo` del formulario de solicitud: lo que se afirma de la respuesta anterior se
      // limpia donde empieza la nueva.
      self.error403 = false;
      self.carril.lanzar(RhSolicitudesVacaciones.resumen(self.anio), {
        exito: function(res) {
          self.resumen = res;
          // El año lo fija el servidor en la primera carga (el que corre, en su zona horaria): la
          // pantalla no tiene por qué saber qué día es en Madrid.
          if (res && res.anio) self.anio = res.anio;
          if (res && res.aniosDisponibles) self.anios = res.aniosDisponibles;
        },
        limpiar: function() {
          // Si falla, lo que queda en pantalla es el resumen de ANTES de la acción, con la
          // solicitud recién anulada todavía puesta. Se vacía y se dice que no se pudo cargar.
          self.resumen = null;
        },
        fallo: function(e) {
          if (self.es403(e)) self.error403 = true;
          else app.dialog.alert(mensajeDelServidor(e, self.$t('misVacaciones.errorCargando')));
        }
      });
    },
    abrirSolicitud: function() {
      this.solicitudAbierta = true;
    },
    /**
     * Lo que pasa cuando el formulario ha enviado: se cierra y se recarga el resumen, que es lo
     * único que sabe cuántos días quedan y qué solicitudes hay.
     *
     * **El reloj lo enciende esta recarga y no se apaga hasta que conteste** (ver `cargar`): el
     * envío ya ha soltado el suyo antes de avisar, así que lo que queda puesto es el de aquí. Sin
     * eso, con una respuesta lenta el popup se cerraba y quedaban a la vista el saldo y las
     * solicitudes de antes del envío sin ninguna señal de que se estaban actualizando (revisión
     * CODEX del PR #1733, ronda 11).
     *
     * **Y el resumen NO se vacía aquí**, al contrario que al cambiar de año. Ya no es porque
     * vaciarlo destruyera el formulario —no cuelga de ningún `v-if`, justamente por esto—, sino
     * por §9: al cambiar de año el título decía 2025 sobre las cifras de 2026 —dos partes de la
     * pantalla diciendo lo contrario—, y aquí el año es el mismo y lo único que pasa es que las
     * cifras todavía son las de hace un segundo, con el reloj encima diciéndolo.
     *
     * **Y si la pantalla ya no está delante, esta recarga no pide nada**: el envío puede terminar
     * después de que alguien se haya ido de aquí, y la ruta es `keepAlive`. De eso responde
     * `cargar`, que es por donde pasan las cuatro recargas, y no este manejador.
     */
    /** «registrada por Ana Pérez». El nombre lo resuelve el servidor; el username es el respaldo. */
    registradaPor: function(solicitud) {
      return this.$t('misVacaciones.registradaPor', {
        quien: solicitud.registradaPorNombre || solicitud.registradaPor
      });
    },
    /**
     * Tras registrar en nombre de otra persona (#70). Se recarga igual que tras enviar una
     * solicitud, y por el mismo camino: lo registrado puede no ser de esta persona ni de este año
     * —lo normal es que no lo sea—, así que lo habitual es que la pantalla quede exactamente
     * igual. Recargar es lo correcto para el caso en que sí la toque y evita tener que decidir
     * aquí si toca o no.
     *
     * Se devuelve la promesa de `cargar()`: quien avisa es la ventana, que no se cierra ni suelta
     * su reloj hasta que el resumen está al día (el mismo contrato que `trasEnviar`).
     */
    trasRegistrar: function() {
      this.registroAbierto = false;
      return this.cargar();
    },
    trasEnviar: function() {
      this.solicitudAbierta = false;
      this.cargar();
    },
    /**
     * Si **esa** solicitud se está anulando ahora mismo. `anulandoId` es de esta instancia y dice
     * cuál se pulsó aquí; el cerrojo es de la solicitud y vive en el módulo, así que contesta que
     * sí también cuando la pantalla se ha recreado con el POST todavía en vuelo.
     */
    anulandose: function(solicitud) {
      const id = solicitud && solicitud.id;
      return this.anulandoId === id || this.accionEnVueloSobre(claveDeSolicitud(id));
    },
    /**
     * **Una anulación a la vez.** El diálogo se cierra al confirmar, pero la fila y su botón seguían
     * habilitados hasta que contestaba el POST, así que otro toque mandaba una segunda anulación con
     * la misma `version`: el control optimista del servidor evita anular dos veces, pero una de las
     * dos peticiones terminaba fallando y su `catch` escribía «no se ha podido anular» —y apagaba el
     * reloj— después de que la otra hubiera ido bien y lanzado la recarga (revisión CODEX del PR
     * #1733, ronda 10).
     *
     * `anulandoId` se queda puesto mientras dura —desde que se pulsa, no desde que se confirma—, y
     * es lo mismo que apaga los botones de la lista. Se suelta en los tres caminos, el bueno, el
     * malo y el de cancelar —dejarlo puesto convertiría un fallo de red, o un «no», en una lista
     * que ya no deja anular nada—, y lo suelta **esta** anulación y no otra: por su propio testigo,
     * en `concluirAnulacion`.
     *
     * **Y es una acción diferida**, igual que el registro en nombre de otra persona: el POST no sale
     * al pulsar, sale cuando alguien contesta a un «¿Continuar?» que monta el marco de la aplicación
     * por encima de la pantalla. Así que la acción se nombra **antes** del diálogo —con el sello de
     * la sesión y la `version` de la fila que se está mirando— y el punto previo al POST la rechaza
     * si esa sesión ya no está delante. Aquí no lo ha señalado nadie: es la misma forma del hallazgo
     * r4302182229, que era de la ventana de registrar, y el daño es el mismo —anular la solicitud
     * que tenía delante la cuenta anterior, a nombre de quien acaba de entrar—. Ver
     * `RhSolicitudesVacaciones.accionDeEstaSesion`.
     *
     * **Ojo al mezclar con la #59**: la bandeja y el calendario del aprobador resuelven sus cuatro
     * diálogos por `accionDeLaSesion`, que da la misma garantía de sesión y añade el sello de la
     * pantalla y un cerrojo por solicitud compartido con aprobar y denegar. Son dos mecanismos para
     * lo mismo y conviene unificarlos, pero eso es una decisión de diseño con su propia ficha: aquí
     * se deja el que ya estaba medido en `scripts/prueba-vacaciones-sesion-y-carril.js`.
     */
    anular: function(solicitud) {
      const self = this;
      const app = self.$f7;
      if (self.anulandoId !== null) return;
      const accion = RhSolicitudesVacaciones.accionDeEstaSesion({
        id: solicitud.id, codigo: solicitud.codigo, version: solicitud.version
      });
      // **El cerrojo se coge aquí, al nombrar la acción, y no cuando se confirma.** Es el mismo
      // defecto del hallazgo r4303373381, que era de la ventana de registrar, y aquí no lo ha
      // señalado nadie: entre la pulsación y el DELETE hay un «¿Continuar?», así que el botón
      // seguía vivo y dos pulsaciones abrían dos diálogos. Con el cerrojo puesto solo al confirmar,
      // la segunda cadena lo encontraba libre si la primera terminaba antes —y salía una segunda
      // anulación con la `version` de entonces, que el servidor rechaza y que acaba en un «no se ha
      // podido anular» sobre una solicitud que sí se anuló—.
      //
      // El testigo nace con él y sin reloj: el reloj es de la petición, y aquí todavía no hay
      // ninguna. Lo coge la continuación, sobre este mismo testigo, para que las dos cosas se
      // suelten en un solo sitio (`concluirAnulacion`).
      const anulacion = { reloj: null };
      self.anulandoId = accion.datos.id;
      self.anulacionEnCurso = anulacion;
      // Anular quita los días del calendario y devuelve el saldo: se confirma para que no se haga
      // de un clic despistado desde la lista.
      //
      // Por el adaptador y no con `app.dialog.confirm` a secas: así el diálogo queda apuntado a la
      // acción y el cierre de sesión lo cierra, en vez de dejar el código de la solicitud de la
      // cuenta anterior pintado sobre el login. Es la misma forma del hallazgo r4302931769, que era
      // del aviso del saldo de la ventana de registrar; aquí no lo ha señalado nadie.
      RhSolicitudesVacaciones.preguntarEnEstaSesion(app, accion,
        self.$t('misVacaciones.confirmaAnular', { codigo: solicitud.codigo }),
        self.$t('misVacaciones.titulo'),
        function() {
          // Lo primero: si la sesión ha cambiado mientras el diálogo estaba abierto, esta anulación
          // no es de quien está delante. Lo comprueba ya `preguntarEnEstaSesion`, y se repite aquí
          // porque esta es la garantía y no el cierre del diálogo, que es cosmético.
          if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
            self.concluirAnulacion(anulacion);
            return;
          }
          // El reloj de ESTA anulación, con su propia clave: concluir ella no apaga el de una
          // lectura que siga en vuelo, ni al contrario (ver `cogerReloj`).
          anulacion.reloj = self.cogerReloj('anular|' + accion.datos.id);
          // La `version` es la que traía la fila cuando se pulsó, no la que haya ahora: es lo que
          // el servidor compara para cortar dos resoluciones a la vez.
          RhSolicitudesVacaciones.anular(accion.datos.id, accion.datos.version)
            .then(function() {
              // Se concluye ANTES de lanzar la recarga, que coge su propio reloj: así la última en
              // cogerlo es la que lo mantiene puesto hasta su respuesta. Al revés —soltarlo
              // después— quedaba el saldo de antes de anular a la vista como si ya fuera el
              // definitivo. Es el mismo defecto del cambio de año.
              self.concluirAnulacion(anulacion);
              // El aviso sale aunque ya se haya salido de la pantalla: es el resultado de algo que
              // esa persona hizo, y de si le corresponde responde la **sesión** —el adaptador— y no
              // qué pantalla esté mirando. Es el mismo criterio que el aviso del envío.
              app.toast.create({
                text: self.$t('misVacaciones.anulada'), position: 'center', closeTimeout: 2500
              }).open();
              // La recarga, en cambio, es de la pantalla: si ya no está delante no pide nada y la
              // hace la siguiente entrada en la ruta (ver `cargar`).
              self.cargar();
            })
            .catch(function(e) {
              // **Solo suelta quien sigue siendo dueño de lo que suelta**, y de eso responde el
              // testigo: si entre medias ha cambiado la sesión, el reloj que hay puesto es el de
              // la primera carga de quien acaba de entrar, y apagarlo le dejaba la pantalla vacía
              // sin ninguna señal de carga (revisión CODEX del PR #1733, ronda 12). Y si lo que
              // sigue en vuelo es una lectura de esta misma pantalla —«Recargar» o un cambio de
              // año mientras se anulaba—, el reloj tiene que seguir puesto, que es lo que este
              // `catch` apagaba de más (ronda 4 de esta tanda).
              self.concluirAnulacion(anulacion);
              // Y si el adaptador ha cortado un intento repetido, o la respuesta es de otra
              // sesión, el aviso no es para quien está delante: no se escribe nada. Lo decide el
              // adaptador, no este `catch` (ver `sinNadaQueDecir`). Tampoco se recarga: el DELETE
              // de verdad puede seguir en vuelo y su respuesta trae el resumen bueno.
              if (RhSolicitudesVacaciones.sinNadaQueDecir(e)) return;
              // **Lo que se mandó ya no vale, así que se recarga antes de avisar.** Es el mismo
              // defecto que la ventana de registrar en nombre de otra persona (revisión CODEX del
              // PR #1737, ronda 7) y aquí no lo ha señalado nadie todavía: la anulación va con la
              // versión que tiene la fila delante, y el servidor la rechaza a propósito si entre
              // medias alguien ha resuelto esa solicitud. Con la lista anterior en pantalla, cada
              // reintento mandaba la misma versión caduca y «Anular» no podía funcionar nunca.
              self.cargar();
              app.dialog.alert(mensajeDelServidor(e, self.$t('misVacaciones.errorAnulando')));
            });
        },
        // Cancelar es una salida tan válida como las otras dos, y suelta lo mismo por el mismo
        // sitio: sin esto, decir «no» dejaba la lista sin poder anular nada hasta salir y volver.
        //
        // Y si lo que acabó la cadena no fue un «no» sino un **cambio de cuenta**, se dice por
        // qué: es la regla que fijó dirección el 07/10/2026 en la #59 para los diálogos del
        // módulo —«no se ejecuta: se cierra y se dice por qué»—, y la que cumplen los cuatro de
        // aprobar y denegar por `accionDeLaSesion`. Se distingue aquí y no en el adaptador, que
        // abandona igual por los dos caminos y no tiene con qué escribir en la pantalla.
        function() {
          self.concluirAnulacion(anulacion);
          if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
            app.dialog.alert(self.$t('common.sesionCambiada'));
          }
        }
      );
    },
    /**
     * Las caducidades de una fila que **no** caben en la casilla, que son todas menos la primera:
     * esa es la fecha que ya se pinta arriba.
     *
     * La lista llega del servidor ya resuelta y ordenada (`RhCaducidadVacacionesDTO`): aquí no se
     * elige cuál va primero ni se recomponen días, que es el reparto que decidió dirección el
     * 02/10/2026 que es del servidor. Sin la lista —una respuesta anterior a este cambio— no hay
     * líneas extra y la casilla queda como estaba.
     */
    otrasCaducidades: function(fila) {
      return ((fila && fila.caducidades) || []).slice(1);
    },
    /**
     * «y 5 hasta el 30/06/2027, por baja médica», o «y 3 caducados el 31/03/2026» cuando esos días
     * ya se perdieron. El motivo solo se escribe cuando lo hay: que unos días del mismo año duren
     * más que otros es ley (art. 38.3 ET) y sin decirlo parecería un error.
     */
    textoCaducidad: function(caducidad) {
      const dias = caducidad.dias || 0;
      const hasta = this.fecha(caducidad.hasta);
      if (caducidad.caducada) {
        return this.$t('misVacaciones.caducidadPerdida', { dias: dias, hasta: hasta });
      }
      const texto = this.$t('misVacaciones.caducidadExtra', { dias: dias, hasta: hasta });
      return caducidad.porIncapacidadTemporal
        ? texto + ', ' + this.$t('misVacaciones.porBajaMedica') : texto;
    },

    // --- #69 · pedir anular una ya aprobada, y anular directamente desde la bandeja -------------

    /** Abre «Pedir anulación» sobre esa solicitud. */
    abrirPeticion: function(solicitud) {
      this.solicitudAAnular = solicitud;
    },
    /** Y la de anular sin petición, que llega desde la bandeja del aprobador. */
    abrirAnulacionDirecta: function(solicitud) {
      this.solicitudAAnularDirecto = solicitud;
    },
    /**
     * Tras pedir la anulación se recarga **el resumen propio**: la solicitud ahora lleva su
     * petición y ya no ofrece «Pedir anulación». El calendario no ha cambiado —los días siguen
     * confirmados— pero lo que la pantalla enseña de esa solicitud, sí.
     *
     * **Cerrar la ventana no se hace aquí**, y es a propósito: la cierra ella con `cerrar` cuando
     * la respuesta es de la solicitud que tiene abierta. Puesto aquí, una respuesta que llegaba
     * después de haberla reabierto sobre otra solicitud cerraba esa otra (revisión CODEX del PR
     * #1748, ronda 9).
     */
    trasPedirAnulacion: function() {
      this.cargar();
    },
    /**
     * Tras anular directamente se recargan **las dos fuentes**. La bandeja, porque esa fila sale
     * de «Anulaciones» y los cuatro contadores cambian; y el resumen propio, porque un ADMIN puede
     * estar anulando días de alguien y mirando después sus propias vacaciones, y sobre todo porque
     * «Recargar» ya recarga las dos: dejar una fuera volvería a abrir la diferencia que la #59
     * cerró.
     *
     * **Y las dos las pide `cargar()`, que es por donde pasa el resto de la pantalla.** Pedir aquí
     * además la bandeja mandaba dos GET idénticos y simultáneos: el carril se queda con el
     * resultado del segundo, pero la primera petición ya se ha ido y el servidor la atiende igual
     * (revisión CODEX del PR #1748). Esto es lo que hacen también `resuelta`, `trasEnviar` y
     * `trasPedirAnulacion`: una sola llamada a `cargar()`.
     *
     * **El badge del menú no está aquí, y es a propósito.** Lo avisa la propia ventana por el
     * manejador `fuera` de `accionDeLaSesion`, igual que la bandeja y el calendario: esto cuelga de
     * `anulada`, que es un efecto DE LA PANTALLA y se descarta si quien anuló ya se ha ido de
     * Vacaciones, y entonces el contador se quedaba con el de antes el resto de la sesión
     * (revisión CODEX del PR #1748, ronda 8). El número del menú es del menú.
     *
     * **Y cerrar la ventana tampoco está aquí**, por lo mismo que en `trasPedirAnulacion`: la
     * cierra ella, que es la única que sabe si la respuesta es de la solicitud que tiene delante.
     */
    trasAnularDirecto: function() {
      this.cargar();
    },
    /** Quién tiene que aceptar la anulación, para la nota al pie de la ventana. */
    aprobadorDe: function(solicitud) {
      const r = this.resumenVigente;
      return (solicitud && (solicitud.aprobadorNombre || solicitud.aprobador))
        || (r && (r.aprobadorNombre || r.aprobador))
        || this.$t('anularVacaciones.quienApruebe');
    },
    /** En qué estado está la petición de anulación, para el color de la línea. */
    claveAnulacion: function(solicitud) {
      const estado = solicitud.peticionAnulacion && solicitud.peticionAnulacion.estado;
      if (estado === 'PENDIENTE') return 'pendiente';
      if (estado === 'ACEPTADA') return 'aceptada';
      return 'cerrada';
    },
    /**
     * Qué se dice de la petición según cómo haya acabado. Son cinco estados y cada uno significa
     * algo distinto para quien lo pidió: que espera, que se aceptó, que se rechazó —y por qué—,
     * que caducó sin efecto, o que se resolvió porque el aprobador anuló esos días por su cuenta.
     */
    textoPeticion: function(solicitud) {
      const p = solicitud.peticionAnulacion;
      const estado = p.estado;
      const n = p.diasQuePide || 0;
      if (estado === 'PENDIENTE') {
        return this.$t('anularVacaciones.peticionPendiente', { n: n });
      }
      if (estado === 'ACEPTADA') {
        return this.$t('anularVacaciones.peticionAceptada', { n: n });
      }
      if (estado === 'RECHAZADA') {
        return this.$t('anularVacaciones.peticionRechazada', {
          motivo: p.motivoResolucion || ''
        });
      }
      if (estado === 'CADUCADA') {
        return this.$t('anularVacaciones.peticionCaducada');
      }
      return this.$t('anularVacaciones.peticionPorAnulacionDirecta');
    },
    /** Los días de esta solicitud que se anularon, con su motivo ya escrito por el servidor. */
    diasAnulados: function(solicitud) {
      return (solicitud.diasExcluidos || []).filter(function(dia) {
        return dia.motivo === 'ANULADO';
      });
    },
    /** «13 oct», el mismo formato corto del resto de la pantalla. */
    corta: function(iso) {
      return this.fechaCorta(iso);
    },
    claveEstado: function(estado) {
      if (estado === 'CONFIRMADA') return 'confirmadas';
      if (estado === 'REALIZADA') return 'disfrutadas';
      if (estado === 'PENDIENTE') return 'solicitadas';
      return 'cerradas';
    },
    etiquetaDe: function(solicitud) {
      return this.$t('misVacaciones.estado_' + solicitud.estado);
    },
    /**
     * Las fechas de una solicitud: **un tramo por tramo pedido**, no su envoltura.
     *
     * «21 dic – 30 dic» cuando es un tramo seguido, «1 dic – 2 dic · 20 dic – 21 dic» cuando son
     * dos separados, y un solo día cuando empieza y acaba el mismo. Una solicitud puede llevar
     * varios tramos —es lo que pide la #58 y lo que deja escrito el servidor en `tramos`—, y
     * pintando solo `fechaInicio`–`fechaFin` esos dos tramos de diciembre aparecían como «1 dic –
     * 21 dic», tanto en las listas como en el historial: daba a entender que incluía las tres
     * semanas de en medio, que es justo lo que la persona NO pidió (revisión CODEX del PR #1733,
     * ronda 6).
     *
     * Sin `tramos` se cae a la envoltura, que es lo único que hay: las solicitudes **migradas** de
     * la #63 se cargaron sin tramos. Y si tampoco tienen `fechaInicio` —la migración no lo
     * escribe— se compone con el primer y el último día de los que ocupa, para que la fila no
     * salga con la celda de fechas vacía.
     */
    fechasDe: function(solicitud) {
      const tramos = (solicitud && solicitud.tramos) || [];
      if (tramos.length) {
        const self = this;
        return tramos.map(function(t) { return self.tramo(t.desde, t.hasta); }).join(' · ');
      }
      const dias = (solicitud && solicitud.dias) || [];
      const desde = solicitud.fechaInicio || dias[0];
      const hasta = solicitud.fechaFin || dias[dias.length - 1];
      return this.tramo(desde, hasta);
    },
    /** Un tramo ya formateado: «21 dic – 30 dic», o el día suelto si empieza y acaba el mismo. */
    tramo: function(desde, hasta) {
      const uno = this.fechaCorta(desde);
      const otro = this.fechaCorta(hasta);
      return uno === otro ? uno : uno + ' – ' + otro;
    },
    /**
     * La segunda línea de cada solicitud. Dice quién la aprobó cuando ya está resuelta y cuándo se
     * pidió cuando todavía no, que es lo que hace falta saber en cada caso.
     */
    detalleDe: function(solicitud) {
      const dias = this.$t('misVacaciones.nDias', { dias: solicitud.diasQueDescuenta });
      // La marca «registrada por» va antes que todo lo demás (#70, punto 6): estas vacaciones no
      // las pidió esta persona, y decir «aprobadas por X» sin más daría a entender que sí.
      if (solicitud.registradaPor) {
        return dias + ' · ' + this.registradaPor(solicitud) + ' · ' + solicitud.codigo;
      }
      if (solicitud.estado === 'CONFIRMADA' && (solicitud.resueltaPor || solicitud.aprobadorNombre)) {
        return dias + ' · ' + this.$t('misVacaciones.aprobadaPor', {
          quien: solicitud.aprobadorNombre || solicitud.resueltaPor
        }) + ' · ' + solicitud.codigo;
      }
      if (solicitud.estado === 'REALIZADA') {
        return this.$t('misVacaciones.nDiasDisfrutados', { dias: solicitud.diasQueDescuenta })
          + ' · ' + solicitud.codigo;
      }
      return dias + ' · ' + this.$t('misVacaciones.solicitadaEl', {
        fecha: this.fechaHora(solicitud.fechaCreacion, true)
      }) + ' · ' + solicitud.codigo;
    },
    ultimoMovimiento: function(solicitud) {
      const historico = solicitud.historico || [];
      return historico.length ? historico[historico.length - 1] : null;
    },
    accion: function(movimiento) {
      const quien = movimiento.usuarioNombre || movimiento.usuario || '';
      return this.$t('misVacaciones.accion_' + movimiento.accion, { quien: quien });
    },
    /**
     * El ancho de un tramo sobre el total de la barra (`totalDeLaBarra`). Nada baja de cero: un
     * ancho negativo lo ignora el navegador, así que el tramo se pintaba igual de vacío pero sin
     * decir que se había quedado fuera de la cuenta.
     */
    ancho: function(dias) {
      const total = this.totalDeLaBarra;
      if (!total) return '0%';
      return (100 * Math.max(0, dias || 0) / total) + '%';
    },
    /**
     * #1158 · Lo que el servidor dice que hay entre la cifra de partida de la bolsa del año y lo que
     * esa bolsa suma en la tarjeta, **solo si cuadra** con lo que la tarjeta pinta y hay algo que
     * explicar. Aquí no se suma nada para calcular: se comprueba. Si un día no cuadrara —una
     * respuesta de una API anterior, un año ya caducado—, la tarjeta vuelve a su número de siempre
     * antes que enseñar una explicación que no es la de ese número.
     */
    composicionBolsaDelAnio: function(diasDeLaTarjeta) {
      const c = this.saldo && this.saldo.composicionBolsaDelAnio;
      if (!c || !c.movimientos || !c.movimientos.length || c.total !== diasDeLaTarjeta) return null;
      const suma = c.movimientos.reduce(function(total, m) { return total + (m.dias || 0); }, c.partida || 0);
      return suma === c.total ? c : null;
    },
    /**
     * #1158 · Una línea de lo que mueve la bolsa del año, con su signo delante: «− 2 de ajuste de
     * RRHH (28/10/2026)». El signo va escrito porque es lo que deja leer la frase como una cuenta.
     */
    textoMovimiento: function(m) {
      const dias = m.dias || 0;
      const valores = {
        signo: dias < 0 ? '−' : '+',
        dias: Math.abs(dias),
        fecha: m.fecha ? this.fecha(m.fecha) : '',
        anio: m.anio
      };
      if (m.concepto === 'PUESTOS_EN_EL_CORTE') return this.$t('misVacaciones.movPuestosEnElCorte', valores);
      if (m.concepto === 'DEVUELTOS_TRAS_EL_CORTE') return this.$t('misVacaciones.movDevueltosTrasElCorte', valores);
      if (m.concepto === 'RESERVADOS_ANIO_SIGUIENTE') return this.$t('misVacaciones.movReservadosAnioSiguiente', valores);
      let clave = 'misVacaciones.movAjuste';
      if (m.tipo === 'AJUSTE_RRHH') clave = 'misVacaciones.movAjusteRrhh';
      else if (m.tipo === 'RETIRADA_INCAPACIDAD_TEMPORAL') clave = 'misVacaciones.movRetiradaIt';
      else if (m.origen === 'MIGRACION') clave = 'misVacaciones.movApuntadosTrasElCorte';
      const texto = this.$t(clave, valores);
      return valores.fecha ? texto + ' (' + valores.fecha + ')' : texto;
    },
    /** `LocalDate` llega como «2026-03-31». Se pinta sin parsearlo a Date: así no se mueve de día. */
    fecha: function(iso) {
      if (!iso) return '';
      const trozos = String(iso).substring(0, 10).split('-');
      return trozos.length === 3 ? trozos[2] + '/' + trozos[1] + '/' + trozos[0] : String(iso);
    },
    /** «21 dic». Se compone a mano por lo mismo: el día no puede moverse por la zona horaria. */
    fechaCorta: function(iso) {
      if (!iso) return '';
      const trozos = String(iso).substring(0, 10).split('-');
      if (trozos.length !== 3) return String(iso);
      return parseInt(trozos[2], 10) + ' ' + this.$t('misVacaciones.mes_' + parseInt(trozos[1], 10));
    },
    /**
     * Las marcas de tiempo del histórico sí son instantes (`Date` de Java), así que estas van por
     * `Date` y se enseñan en la zona del navegador, que es donde está quien lo lee.
     */
    fechaHora: function(valor, soloFecha) {
      if (!valor) return '';
      const d = new Date(valor);
      if (isNaN(d.getTime())) return String(valor);
      const dia = ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2)
        + '/' + d.getFullYear();
      if (soloFecha) return dia;
      return dia + ' ' + ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
    }
  }
};
</script>
